import React from 'react'
import { createRoot } from 'react-dom/client'
import { debounce } from '@/tiptap/lib/debounce'
import { Mark } from '@tiptap/core'
import { Plugin, TextSelection } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { nanoid } from 'nanoid'
import {
  FloatingCommentWrapper,
  type FloatingCommentWrapperProps,
} from './floating-comment-wrapper'
import type {
  Comment,
  CommentOptions,
  CommentScope,
  CommentStorage,
} from './types'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    comments: {
      /**
       * Sets and adds a comment to a selection
       */
      setComment: (content: string) => ReturnType
      /**
       * Unsets and removes a comment
       */
      unsetComment: () => ReturnType
      /**
       * Removes a comment by id
       */
      commentRemove: (
        commentId: string | { commentId: string; skipRemote?: boolean }
      ) => ReturnType
      /**
       * Resolves a comment by id
       */
      commentResolve: (
        commentId: string,
        resolution: 'RESOLVED' | 'DISMISSED' | 'IMPLEMENTED'
      ) => ReturnType
      /**
       * Update all comments (for syncing)
       */
      updateComments: (comments: Comment[]) => ReturnType
      /**
       * Reload comments from storage
       */
      reloadComments: (forceRefresh?: boolean) => ReturnType
      /**
       * Add a page-level comment
       */
      addPageComment: (content: string, pageSection?: string) => ReturnType
      /**
       * Add a block-level comment
       */
      addBlockComment: (blockId: string, content: string, extra?: { mentions?: string[]; richContent?: any }) => ReturnType
      /**
       * Test mark application
       */
      testMark: () => ReturnType
      /**
       * Dismiss a check comment
       */
      dismissCheck: (commentId: string) => ReturnType
      /**
       * Apply a check suggestion
       */
      applyCheckSuggestion: (commentId: string) => ReturnType
      /**
       * Edit a comment
       */
      editComment: (commentId: string, content: string, metadata?: any) => ReturnType
      /**
       * Open a comment panel
       */
      openComment: (commentId: string) => ReturnType
      /**
       * Delete a comment
       */
      deleteComment: (
        commentId: string | { commentId: string; skipRemote?: boolean }
      ) => ReturnType
      /**
       * Clear comment cache
       */
      clearCommentCache: () => ReturnType
      /**
       * Add a fake suggestion comment for testing
       */
      addFakeSuggestionComment: () => ReturnType
      /**
       * Highlight a block by id without opening floating panel
       */
      highlightBlock: (blockId: string) => ReturnType
      /**
       * Apply a suggestion from a comment
       */
      applySuggestion: (commentId: string) => ReturnType
    }
  }

  interface ExtensionMethods {
    loadComments: () => void
  }
}

export const CommentsExtension = Mark.create<CommentOptions, CommentStorage>({
  name: 'comment',

  addOptions() {
    return {
      HTMLAttributes: {},
      storage: null as any,
      docId: '',
      workspaceId: '',
      branchId: undefined,
      changeRequestId: undefined,
      onCommentsUpdated: () => {},
      onCommentActivated: () => {},
      onCommentsDataUpdated: () => {},
      defaultColorClass: '',
      onError: undefined,
      readOnly:false
    }
  },

  addStorage() {
    return {
      comments: new Map(),
      scrollHandler: null,
      pendingNewComment: null,
      expandedBlocks: new Set<string>(),
      needsInlineCommentUpdate: false,
      suppressNextSelection: false,
      isLoadingComments: false, // 添加加载状态标记
      // 活动的 RANGE 评论，用于在编辑器中常亮高亮
      activeRangeCommentId: null as string | null,
      // 活动的 BLOCK，用于仅高亮块（不打开浮窗）
      activeBlockId: null as string | null,
      // 仅允许在创建流程中渲染的 RANGE 浮窗白名单（元素形如 'range-<id>'）
      allowedRangePanels: new Set<string>(),
    }
  },

  onCreate() {
    // Initialize empty storage
    this.storage.comments = new Map()
    this.storage.expandedBlocks = new Set()

    // Initial load
    if (this.editor) {
      this.editor.commands.reloadComments()
    }
  },

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: (el) =>
          (el as HTMLSpanElement).getAttribute('data-comment-id'),
        renderHTML: (attrs) => ({
          'data-comment-id': attrs.commentId || '',
        }),
      },
      // Carry source/severity on the mark so initial render can color without waiting for storage
      source: {
        default: null,
        parseHTML: (el) =>
          (el as HTMLSpanElement).getAttribute('data-comment-source'),
        renderHTML: (attrs) => ({
          'data-comment-source': attrs.source || undefined,
        }),
      },
      severity: {
        default: null,
        parseHTML: (el) =>
          (el as HTMLSpanElement).getAttribute('data-comment-severity'),
        renderHTML: (attrs) => ({
          'data-comment-severity': attrs.severity || undefined,
        }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-comment-id]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const commentId = HTMLAttributes['data-comment-id'] || ''

    const classes: string[] = []
    if (commentId) {
      const markSource = HTMLAttributes['data-comment-source'] || undefined
      const markSeverity = HTMLAttributes['data-comment-severity'] || undefined

      if (markSource) {
        classes.push(`comment-source-${String(markSource).toLowerCase()}`)
        if (markSource === 'AI') classes.push('comment-ai')
        if (markSource === 'USER') classes.push('comment-user')
        if (markSource === 'AUTOMATIC_CHECK') {
          classes.push('comment-automatic-check')
          if (markSeverity) classes.push(`comment-check-${markSeverity}`)
        }
      } else if (this.storage?.comments) {
        const comment = this.storage.comments.get(commentId)
        const source = comment?.source
      if (source) {
        classes.push(`comment-source-${String(source).toLowerCase()}`)
      }
      if (source === 'AUTOMATIC_CHECK') {
        classes.push('comment-automatic-check')
          const sev = comment?.metadata?.severity
          if (typeof sev === 'string' && sev.length > 0) {
            classes.push(`comment-check-${sev}`)
          }
        }
        if (source === 'AI') classes.push('comment-ai')
        if (source === 'USER') classes.push('comment-user')
      }
    }

    return [
      'span',
      {
        'data-comment-id': commentId,
        class: classes.length > 0 ? classes.join(' ') : undefined,
        'data-comment-source': HTMLAttributes['data-comment-source'] || undefined,
        'data-comment-severity': HTMLAttributes['data-comment-severity'] || undefined,
      },
    ]
  },

  onSelectionUpdate() {
    if (!this.editor?.state?.selection || !this.options) return

    // 一次性抑制：在我们程序性地关闭浮窗/移除 mark 后，忽略下一次 selection 更新，避免闪烁
    if ((this.storage as any)?.suppressNextSelection) {
      ;(this.storage as any).suppressNextSelection = false
      return
    }

    const selection = this.editor?.state?.selection
    if (!('$from' in selection)) return

    const marks = selection.$from.marks()
    if (!marks.length) {
      this.options.onCommentActivated(null)
      return
    }

    const activeCommentMark = marks.find((mark) => mark.type.name === 'comment')
    if (!activeCommentMark) {
      this.options.onCommentActivated(null)
      return
    }

    this.options.onCommentActivated(activeCommentMark?.attrs?.commentId || null)
  },

  addProseMirrorPlugins() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const extension = this

    return [
      new Plugin({
        props: {
          handleClick: (view, pos, event) => {
            const { doc } = view.state
            const node = doc.nodeAt(pos)
            if (!node) return false

            // Get ALL comment marks, not just the first one
            const commentMarks = node.marks.filter((m) => m.type.name === 'comment')

            if (commentMarks.length > 0) {
              event.preventDefault()
              event.stopPropagation()

              // Collect all comments on this text range
              const comments: Comment[] = []
              const commentIds: string[] = []

              for (const mark of commentMarks) {
                if (mark.attrs.commentId) {
                  const comment = extension.storage.comments.get(mark.attrs.commentId)
                  if (comment && comment.scope === 'RANGE') {
                    comments.push(comment)
                    commentIds.push(mark.attrs.commentId)
                  }
                }
              }

              console.log('Comments clicked:', {
                commentIds,
                comments,
                count: comments.length,
                hasReplies: comments.map(c => ({
                  id: c.id,
                  repliesCount: c.replies?.length || 0,
                  replies: c.replies
                })),
                allComments: Array.from(extension.storage.comments.entries()),
              })

              if (comments.length > 0) {
                // If multiple comments, pass all of them; otherwise pass single comment
                const primaryComment = comments[0]
                extension.storage.activeRangeCommentId = primaryComment.id

                // Pass multiple comments if available
                if (comments.length > 1) {
                  // Pass array of comments for multiple threads
                  extension.options.onCommentActivated(primaryComment.id, comments)
                } else {
                  // Pass single comment
                  extension.options.onCommentActivated(primaryComment.id, primaryComment)
                }

                // 触发一次装饰刷新
                if (extension.editor) {
                  const { state, dispatch } = extension.editor.view
                  dispatch(state.tr.setMeta('addToHistory', false))
                }
                return true
              }

              // Fallback for non-RANGE comments
              if (commentMarks[0] && commentMarks[0].attrs.commentId) {
                extension.options.onCommentActivated(commentMarks[0].attrs.commentId)
                return true
              }
            }
            return false
          },
          decorations: (state) => {
            const decorations: Decoration[] = []

            // Create decorations for blocks with comments
            if (extension.storage.comments) {
              const blockComments = new Map<string, Comment[]>()

              // First, collect parent comments by block ID
              const parentsByBlock = new Map<string, Set<string>>()

              extension.storage.comments.forEach((comment) => {
                const blockId = comment.docBlockId || (comment as any)?.metadata?.blockId

                if (
                  blockId &&
                  !comment.parentId &&
                  comment.commentStatus !== 'RESOLVED' &&
                  comment.commentStatus !== 'DISMISSED' &&
                  comment.commentStatus !== 'IMPLEMENTED'
                ) {
                  // Include both BLOCK comments and RANGE comments that belong to this block
                  if (comment.scope === 'BLOCK' || comment.scope === 'RANGE') {
                    if (!blockComments.has(blockId)) {
                      blockComments.set(blockId, [])
                      parentsByBlock.set(blockId, new Set())
                    }
                    blockComments.get(blockId)!.push(comment)
                    parentsByBlock.get(blockId)!.add(comment.id)
                  }
                }
              })

              // Then, add replies to the appropriate blocks
              extension.storage.comments.forEach((comment) => {
                if (
                  comment.parentId &&
                  comment.commentStatus !== 'RESOLVED' &&
                  comment.commentStatus !== 'DISMISSED' &&
                  comment.commentStatus !== 'IMPLEMENTED'
                ) {
                  // Find which block this reply belongs to
                  for (const [blockId, parentIds] of parentsByBlock.entries()) {
                    if (parentIds.has(comment.parentId)) {
                      blockComments.get(blockId)!.push(comment)
                      // Also add this reply ID in case of nested replies
                      parentIds.add(comment.id)
                      break
                    }
                  }
                }
              })

              // Add decorations for blocks（含活动块高亮行，仅背景）
              state.doc.descendants((node, pos) => {
                // Check both id and data-id attributes
                const nodeId = node.attrs.id || node.attrs['data-id']
                if (!nodeId) return

                const hasComments = blockComments.has(nodeId)
                const isExpanded = extension.storage.expandedBlocks.has(nodeId)
                const isActiveBlock = extension.storage.activeBlockId === nodeId

                if (isActiveBlock) {
                  decorations.push(
                    Decoration.node(pos, pos + node.nodeSize, {
                      class: 'block-comment-active bg-amber-50',
                      'data-block-id': nodeId,
                    })
                  )
                } else if (isExpanded) {
                  // Highlight ALL expanded blocks (in comment mode)
                  decorations.push(
                    Decoration.node(pos, pos + node.nodeSize, {
                      class: 'block-comment-active',
                      'data-block-id': nodeId,
                    })
                  )
                } else if (hasComments) {
                  // Only track blocks with comments (no visual highlight unless expanded)
                  const comments = blockComments.get(nodeId)!
                  decorations.push(
                    Decoration.node(pos, pos + node.nodeSize, {
                      class: 'has-block-comments',
                      'data-block-id': nodeId,
                      'data-comment-count': comments.length.toString(),
                    })
                  )
                }
              })

              // Add decorations for active range comments（淡黄色背景常亮）
              const activeId = extension.storage.activeRangeCommentId
              if (activeId) {
                state.doc.descendants((node, pos) => {
                  const commentMark = node.marks.find(
                    (m) => m.type.name === 'comment' && m.attrs.commentId === activeId
                  )
                  if (commentMark) {
                    decorations.push(
                      Decoration.inline(pos, pos + node.nodeSize, {
                        class: 'comment-active comment-active-bg',
                        // amber-50 #FFFBEB
                        // style: 'background-color: #FFFBEB;',
                      })
                    )
                  }
                })
              }
            }

            return DecorationSet.create(state.doc, decorations)
          },
        },
        view() {
          let indicatorContainer: HTMLDivElement | null = null
          let updateTimeout: NodeJS.Timeout | null = null
          let hoveredBlockId: string | null = null
          let cleanupFunctions: (() => void)[] = []
          // Single root for all floating panels
          let floatingPanelRoot: any = null
          let floatingPanelContainer: HTMLDivElement | null = null
          const floatingPanels = new Map<string, FloatingCommentWrapperProps>()

          // Global function to close panels
          const closeCommentPanel = (panelKey: string) => {
            console.log('closeCommentPanel called with:', panelKey)
            floatingPanels.delete(panelKey)
            // Also try variations of the key
            floatingPanels.delete(panelKey.replace('range_', 'range-'))
            floatingPanels.delete(panelKey.replace('range-', 'range_'))
            renderFloatingPanels()
          }

          // Make it globally accessible for debugging
          ;(window as any).closeCommentPanel = closeCommentPanel

          const getFloatingPanelContainer = () => {
            if (!floatingPanelContainer) {
              floatingPanelContainer = document.createElement('div')
              floatingPanelContainer.id = 'tiptap-comment-panels'
              document.body.appendChild(floatingPanelContainer)
              floatingPanelRoot = createRoot(floatingPanelContainer)
            }
            return {
              container: floatingPanelContainer,
              root: floatingPanelRoot,
            }
          }

          const renderFloatingPanels = () => {
            // console.log(
            //   'renderFloatingPanels called, panels:',
            //   floatingPanels.size,
            //   Array.from(floatingPanels.keys())
            // )
            const { root } = getFloatingPanelContainer()

            if (floatingPanels.size === 0) {
              root.render(null)
              return
            }

            const panels = Array.from(floatingPanels.entries()).map(
              ([id, props]) => {
                return React.createElement(FloatingCommentWrapper, {
                  key: id,
                  ...props,
                })
              }
            )

            root.render(React.createElement(React.Fragment, null, ...panels))
          }

          const createIndicatorContainer = () => {
            // Find editor wrapper
            const editorElement = extension.editor?.view?.dom
            if (!editorElement) return null

            const editorWrapper =
              editorElement.closest('.editor-content') ||
              editorElement.closest('.editor-container') ||
              editorElement.parentElement
            if (!editorWrapper) return null

            // IMPORTANT: Clean up any stale indicator containers left from previous editor instances
            // This prevents duplicated icon overlays after switching docs (editor remounts).
            const staleContainers = editorWrapper.querySelectorAll(
              '.comment-indicators-container'
            )
            staleContainers.forEach((el) => {
              try {
                el.remove()
              } catch (_e) {
                // ignore
              }
            })

            // Create container for indicators
            const container = document.createElement('div')
            container.className = 'comment-indicators-container'
            container.style.position = 'absolute'
            container.style.top = '0'
            container.style.left = '0'
            container.style.right = '0'
            container.style.bottom = '0'
            container.style.pointerEvents = 'none'
            container.style.zIndex = '100'
            container.style.overflow = 'visible'

            if (editorWrapper instanceof HTMLElement) {
              editorWrapper.style.position = 'relative'
              editorWrapper.appendChild(container)
            }

            return container
          }

          const toggleInlineComments = (blockId: string) => {
            if (extension.storage.expandedBlocks.has(blockId)) {
              // If clicking the same block, just close it
              extension.storage.expandedBlocks.clear()
            } else {
              // Close any existing panels and open the new one
              extension.storage.expandedBlocks.clear()
              extension.storage.expandedBlocks.add(blockId)
            }
            updateInlineComments()
          }

          const updateInlineComments = () => {
            // console.log(
            //   'updateInlineComments called, expandedBlocks:',
            //   Array.from(extension.storage.expandedBlocks)
            // )
            const editorElement = extension.editor?.view?.dom
            if (!editorElement) return

            // Ensure only one indicator container exists after doc switches
            if (!indicatorContainer) {
              indicatorContainer = createIndicatorContainer()
            } else {
              // If for some reason multiple containers exist, remove duplicates
              const wrappers = (extension.editor?.view?.dom as HTMLElement)
                ?.parentElement?.querySelectorAll('.comment-indicators-container')
              if (wrappers && wrappers.length > 1) {
                wrappers.forEach((el, idx) => {
                  if (idx > 0) try { el.remove() } catch { /* noop */ }
                })
              }
            }

            // Check if we need to show the add comment panel
            if (extension.storage.pendingNewComment) {
              const { tempId, from, to, text, blockId } =
                extension.storage.pendingNewComment

              // Find the marked text element
              const markedElements = editorElement.querySelectorAll(
                `span[data-comment-id="${tempId}"]`
              )
              if (markedElements.length > 0) {
                const markedElement = markedElements[0] as HTMLElement

                // Show floating add comment panel
                const panelId = `add-${tempId}`

                floatingPanels.set(panelId, {
                  blockId: blockId,
                  comments: [],
                  isAddingComment: true,
                  rangeText: text,
                  referenceElement: markedElement,
                  onAddComment: (content: string, extra?: { mentions?: string[]; richContent?: any }) => {
                    // Remove the temporary mark
                    if (extension.editor) {
                      const { state, dispatch } = extension.editor.view
                      const tr = state.tr

                      state.doc.nodesBetween(from, to, (node, pos) => {
                        const mark = node.marks.find(
                          (m) =>
                            m.type.name === 'comment' &&
                            m.attrs.commentId === tempId
                        )
                        if (mark) {
                          tr.removeMark(pos, pos + node.nodeSize, mark)
                        }
                      })

                      if (tr.docChanged) {
                        dispatch(tr)
                      }
                    }

                    // Clear pending comment
                    extension.storage.pendingNewComment = null

                    // Create the actual comment using the existing range comment creation logic
                    // Calculate block-relative positions
                    let relativeStart = -1
                    let relativeEnd = -1

                    if (extension.editor) {
                      const { state } = extension.editor.view
                      state.doc.nodesBetween(from, to, (node, pos) => {
                        if (node.attrs?.id === blockId) {
                          // Calculate text offset within the block
                          let textOffset = 0
                          node.descendants((childNode, childPos) => {
                            if (childNode.isText) {
                              const nodeStart = pos + childPos + 1
                              const nodeEnd = nodeStart + childNode.nodeSize

                              // Check if selection starts in this text node
                              if (
                                relativeStart === -1 &&
                                from >= nodeStart &&
                                from <= nodeEnd
                              ) {
                                relativeStart = textOffset + (from - nodeStart)
                              }

                              // Check if selection ends in this text node
                              if (
                                relativeEnd === -1 &&
                                to >= nodeStart &&
                                to <= nodeEnd
                              ) {
                                relativeEnd = textOffset + (to - nodeStart)
                              }

                              textOffset += childNode.text?.length || 0
                            }
                          })
                        }
                      })
                    }

                    // Create comment via API with block-relative positions
                    extension.options.storage
                      .createComment({
                        id: tempId,
                        scope: 'RANGE' as CommentScope,
                        docId: extension.options.docId,
                        workspaceId: extension.options.workspaceId,
                        branchId: extension.options.branchId,
                        changeRequestId: extension.options.changeRequestId,
                        docBlockId: blockId,
                        textRange: {
                          start: relativeStart,
                          end: relativeEnd,
                          text,
                        },
                        content,
                        metadata: extra?.richContent ? { content: extra.richContent } : undefined,
                        ...(extra?.mentions ? { mentions: extra.mentions } as any : {}),
                      })
                      .then((comment) => {
                        // Add comment to storage
                        extension.storage.comments.set(comment.id, comment)
                        extension.options.onCommentsUpdated(
                          Array.from(extension.storage.comments.values())
                        )

                        // Apply the real mark
                        if (extension.editor) {
                          const { state, dispatch } = extension.editor.view
                          const mark = state.schema.marks.comment.create({
                            commentId: comment.id,
                            source: comment.source,
                            severity: comment.metadata?.severity,
                          })
                          const tr = state.tr.addMark(from, to, mark)
                          dispatch(tr)

                          // Wait for DOM update before showing the new panel
                          setTimeout(() => {
                            // Close add panel
                            floatingPanels.delete(panelId)

                            // After creation: do not open a range floating panel. Highlight inline and open side panel.
                            extension.storage.expandedBlocks.clear()
                            extension.storage.activeRangeCommentId = comment.id
                            try { extension.options.onCommentActivated?.(comment.id) } catch (_e) { /* noop */ }
                            updateInlineComments()
                          }, 150)
                        } else {
                          // If no editor, still close panel and update
                          floatingPanels.delete(panelId)
                          extension.storage.expandedBlocks.clear()
                          extension.storage.activeRangeCommentId = comment.id
                          try { extension.options.onCommentActivated?.(comment.id) } catch (_e) { /* noop */ }
                          updateInlineComments()
                        }

                        // 创建 RANGE 评论后刷新缓存并重载
                        try {
                          extension.options.storage
                            .clearCommentsCache(
                              extension.options.docId,
                              extension.options.branchId,
                              extension.options.changeRequestId
                            )
                            .catch(() => {})
                            .finally(() => {
                              try {
                                extension.editor?.commands.reloadComments(true)
                              } catch (_e) { /*noop*/ }
                            })
                        } catch (_e) {
                          try {
                            extension.editor?.commands.reloadComments(true)
                          } catch (_e2) { /*noop*/ }
                        }
                      })
                      .catch((error) => {
                        console.error('Failed to create comment:', error)

                        // If it was a network error, the comment might be saved locally
                        if (
                          error.message?.includes('network') ||
                          error.message?.includes('offline')
                        ) {
                          // Keep the temporary mark visible
                          updateInlineComments()

                          if (extension.options.onError) {
                            extension.options.onError(
                              "Comment saved locally. It will sync when you're back online.",
                              error
                            )
                          }
                        } else {
                          // Remove the temporary mark on other errors
                          if (extension.editor) {
                            const { state, dispatch } = extension.editor.view
                            const tr = state.tr

                            state.doc.nodesBetween(from, to, (node, pos) => {
                              const mark = node.marks.find(
                                (m) =>
                                  m.type.name === 'comment' &&
                                  m.attrs.commentId === tempId
                              )
                              if (mark) {
                                tr.removeMark(pos, pos + node.nodeSize, mark)
                              }
                            })

                            if (tr.docChanged) {
                              dispatch(tr)
                            }
                          }

                          // Clear pending comment
                          extension.storage.pendingNewComment = null
                          updateInlineComments()

                          if (extension.options.onError) {
                            extension.options.onError(
                              'Failed to create comment. Please try again.',
                              error
                            )
                          }
                        }
                      })
                  },
                  onReply: () => {}, // Not applicable for new comment
                  onResolve: () => {}, // Not applicable for new comment
                  onClose: () => {
                    // Remove the temporary mark
                    if (extension.editor) {
                      const { state, dispatch } = extension.editor.view
                      const tr = state.tr

                      state.doc.nodesBetween(from, to, (node, pos) => {
                        const mark = node.marks.find(
                          (m) =>
                            m.type.name === 'comment' &&
                            m.attrs.commentId === tempId
                        )
                        if (mark) {
                          tr.removeMark(pos, pos + node.nodeSize, mark)
                        }
                      })

                      if (tr.docChanged) {
                        dispatch(tr)
                      }
                    }

                    // Clear pending comment and close panel
                    extension.storage.pendingNewComment = null
                    floatingPanels.delete(panelId)
                    renderFloatingPanels()
                    updateInlineComments()
                  },
                })

                renderFloatingPanels()

                return // Don't process other panels when adding a comment
              }
            }

            // Update scroll lock based on whether any blocks are expanded
            const hasOpenPanels =
              floatingPanels.size > 0 ||
              Array.from(extension.storage.expandedBlocks || []).some((id) => !String(id).startsWith('range-')) ||
              Boolean(extension.storage.pendingNewComment)

            if (
              hasOpenPanels &&
              !extension.storage.scrollHandler
            ) {
              // Prevent scroll events instead of modifying overflow styles
              const preventScroll = (e: Event) => {
                // Allow scrolling within the comment panel itself
                const target = e.target as HTMLElement
                if (
                  target.closest('.inline-comment-container') ||
                  target.closest('.comment-panel')
                ) {
                  return
                }

                e.preventDefault()
                e.stopPropagation()
              }

              // Add passive: false to ensure preventDefault works
              const scrollOptions = { passive: false, capture: true }

              // Find the scroll area container
              const scrollArea = document.getElementById('topic-scroll-area')
              if (scrollArea) {
                scrollArea.addEventListener(
                  'wheel',
                  preventScroll,
                  scrollOptions
                )
                scrollArea.addEventListener(
                  'touchmove',
                  preventScroll,
                  scrollOptions
                )
              }

              // Also prevent on document level as fallback
              document.addEventListener('wheel', preventScroll, scrollOptions)
              document.addEventListener(
                'touchmove',
                preventScroll,
                scrollOptions
              )

              // Store handler for cleanup
              if (extension.storage) {
                extension.storage.scrollHandler = preventScroll
              }
            } else if (
              !hasOpenPanels &&
              extension.storage.scrollHandler
            ) {
              // Re-enable scrolling when all comment panels are closed
              const preventScroll = extension.storage.scrollHandler
              const scrollOptions = { passive: false, capture: true }

              const scrollArea = document.getElementById('topic-scroll-area')
              if (scrollArea) {
                scrollArea.removeEventListener(
                  'wheel',
                  preventScroll,
                  scrollOptions
                )
                scrollArea.removeEventListener(
                  'touchmove',
                  preventScroll,
                  scrollOptions
                )
              }

              document.removeEventListener(
                'wheel',
                preventScroll,
                scrollOptions
              )
              document.removeEventListener(
                'touchmove',
                preventScroll,
                scrollOptions
              )

              // Clear handler
              if (extension.storage) {
                extension.storage.scrollHandler = null
              }
            }

            // Process all expanded blocks and range comments
            // console.log(
            //   'Processing expandedBlocks:',
            //   Array.from(extension.storage.expandedBlocks)
            // )
            extension.storage.expandedBlocks.forEach((expandedId) => {
              console.log('Processing expandedId:', expandedId)
              // Check if this is a range comment
              if (expandedId.startsWith('range-')) {
                // 仅当允许名单包含该 range 面板时，才渲染浮窗；否则跳过（仅依赖 activeRangeCommentId 做行高亮）
                if (!extension.storage.allowedRangePanels?.has(expandedId)) {
                  return
                }
                const commentId = expandedId.substring(6) // Remove 'range-' prefix
                console.log('Processing range comment:', commentId)

                const comment = extension.storage.comments.get(commentId)
                console.log('Found comment:', comment)

                if (!comment || comment.scope !== 'RANGE') {
                  return
                }

                // For resolved comments, we still want to show them when clicked

                // Find the marked text for this comment
                let markedElement: HTMLElement | null = null
                const markedElements = editorElement.querySelectorAll(
                  `span[data-comment-id="${commentId}"]`
                )
                console.log(
                  `Looking for comment mark ${commentId}, found ${markedElements.length} elements`
                )
                if (markedElements.length > 0) {
                  markedElement = markedElements[0] as HTMLElement
                }

                if (!markedElement) {
                  console.log(
                    `No marked element found for comment ${commentId}`
                  )
                  return
                }

                // Get all comments for this range comment (including replies)
                const rangeComments: Comment[] = []
                const commentIds = new Set<string>()

                // Always add the main comment when explicitly clicked (even if resolved)
                rangeComments.push(comment)
                commentIds.add(comment.id)

                // Get ALL replies (including nested replies) - keep checking until no new replies are found
                let foundNewReplies = true
                while (foundNewReplies) {
                  foundNewReplies = false
                  extension.storage.comments.forEach((c) => {
                    if (
                      c.parentId &&
                      commentIds.has(c.parentId) &&
                      !commentIds.has(c.id) &&
                      c.commentStatus !== 'RESOLVED' &&
                      c.commentStatus !== 'DISMISSED'
                    ) {
                      rangeComments.push(c)
                      commentIds.add(c.id)
                      foundNewReplies = true
                    }
                  })
                }

                // Only show panel if there are comments to display
                // Note: We always have at least the main comment here since we added it above
                if (rangeComments.length === 0) {
                  // Remove from expanded if no comments left
                  extension.storage.expandedBlocks.delete(expandedId)
                  return
                }

                // Show floating comment panel for range
                const showRangePanel = () => {
                  console.log(
                    `showRangePanel called for ${expandedId}, setting floating panel`
                  )
                  // Helper: create fixed-position placeholder so panel won't jump when mark disappears
                  const ensurePlaceholder = () => {
                    try {
                      const rect = markedElement.getBoundingClientRect()
                      const placeholder = document.createElement('div')
                      placeholder.style.position = 'fixed'
                      placeholder.style.left = `${Math.max(0, rect.left)}px`
                      placeholder.style.top = `${Math.max(0, rect.top)}px`
                      placeholder.style.width = '1px'
                      placeholder.style.height = '1px'
                      placeholder.style.pointerEvents = 'none'
                      placeholder.setAttribute('data-range-anchor', commentId)
                      document.body.appendChild(placeholder)
                      return placeholder
                    } catch (_e) {
                      return null
                    }
                  }
                  const removePlaceholder = (id: string) => {
                    try {
                      const nodes = document.querySelectorAll(`div[data-range-anchor="${id}"]`)
                      nodes.forEach((n) => n.parentNode && n.parentNode.removeChild(n))
                    } catch (_e) { /* noop */ }
                  }
                  floatingPanels.set(expandedId, {
                    blockId: commentId, // Use commentId as blockId for range comments
                    comments: rangeComments,
                    rangeText:
                      comment.textRange?.text ||
                      markedElement.textContent ||
                      '', // Pass the commented text
                    isRangeComment: true, // This is a range comment panel
                    onAddComment: () => {}, // Range comments don't support adding new comments
                    onReply: (parentId: string, content: string) => {
                      extension.options.storage
                        .createReply(parentId, content)
                        .then((reply) => {
                          // Add to local storage immediately
                          if (reply) {
                            extension.storage.comments.set(reply.id, reply)
                            // Store current expanded blocks before updating
                            const currentExpanded = Array.from(
                              extension.storage.expandedBlocks
                            )
                            extension.options.onCommentsUpdated(
                              Array.from(extension.storage.comments.values())
                            )
                            // Restore expanded blocks after update
                            currentExpanded.forEach((id) =>
                              extension.storage.expandedBlocks.add(id)
                            )
                          }

                          // Update the UI without reloading (keeps panel open)
                          setTimeout(() => updateInlineComments(), 100)
                        })
                        .catch((error) => {
                          console.error('Failed to create reply:', error)
                          if (extension.options.onError) {
                            if (
                              error.message?.includes('network') ||
                              error.message?.includes('offline')
                            ) {
                              extension.options.onError(
                                "Reply saved locally. It will sync when you're back online.",
                                error
                              )
                            } else {
                              extension.options.onError(
                                'Failed to create reply. Please try again.',
                                error
                              )
                            }
                          }
                        })
                    },
                    // Single-direction activation: only notify external panel to jump/highlight
                    onThreadActivate: (activatedId: string) => {
                      try {
                        extension.options.onCommentActivated?.(activatedId)
                      } catch (_e) { /* noop */ }
                    },
                    onResolve: (
                      commentId: string,
                      resolution: 'RESOLVED' | 'DISMISSED' | 'IMPLEMENTED' = 'RESOLVED'
                    ) => {
                      // 抑制一次 selection 更新，避免程序性变更导致的闪烁
                      ;(extension.storage as any).suppressNextSelection = true
                      setTimeout(() => {
                        ;(extension.storage as any).suppressNextSelection = false
                      }, 150)
                      // Before the mark is removed, swap reference to a fixed placeholder
                      const ph = ensurePlaceholder()
                      if (ph) {
                        const prev = floatingPanels.get(expandedId)
                        if (prev) {
                          floatingPanels.set(expandedId, { ...prev, referenceElement: ph })
                          renderFloatingPanels()
                        }
                      }

                      extension.editor?.commands.commentResolve(commentId, resolution)

                      // Update the comment status locally
                      const comment = extension.storage.comments.get(commentId)
                      if (comment) {
                        extension.storage.comments.set(commentId, {
                          ...comment,
                          commentStatus: resolution,
                        })
                      }

                      // Check if there are any remaining non-resolved comments for this range
                      const remainingComments = rangeComments.filter(
                        (c) =>
                          c.id !== commentId &&
                          c.commentStatus !== 'RESOLVED' &&
                          c.commentStatus !== 'DISMISSED' &&
                          c.commentStatus !== 'IMPLEMENTED'
                      )

                      const isParent = !comment?.parentId
                      const shouldClose = isParent || remainingComments.length === 0
                      if (shouldClose) {
                        extension.storage.expandedBlocks.delete(expandedId)
                        extension.storage.allowedRangePanels?.delete(expandedId)
                        if (ph) {
                          try { ph.remove() } catch (_) { /* noop */ }
                        }
                      }

                      // Update the panel
                      setTimeout(() => updateInlineComments(), 100)
                    },
                    onClose: () => {
                      extension.storage.expandedBlocks.delete(expandedId)
                      // cleanup placeholder of this range
                      removePlaceholder(commentId)
                      updateInlineComments()

                      if (extension.editor) {
                        const { state, dispatch } = extension.editor.view
                        const tr = state.tr.setMeta('addToHistory', false)
                        dispatch(tr)
                      }
                    },
                    onApplySuggestion: (commentId: string) => {
                      try {
                        const ok = extension.editor?.chain().focus().applySuggestion(commentId).run()
                        if (!ok) {
                      extension.editor?.commands.applySuggestion(commentId)
                        }
                      } catch (_e) {
                        extension.editor?.commands.applySuggestion(commentId)
                      }
                    },
                    onDismissCheck: (commentId: string) => {
                      ;(extension.storage as any).suppressNextSelection = true
                      setTimeout(() => {
                        ;(extension.storage as any).suppressNextSelection = false
                      }, 150)
                      const ph = ensurePlaceholder()
                      if (ph) {
                        const prev = floatingPanels.get(expandedId)
                        if (prev) {
                          floatingPanels.set(expandedId, { ...prev, referenceElement: ph })
                          renderFloatingPanels()
                        }
                      }
                      extension.editor?.commands.dismissCheck(commentId)
                      // Dismiss 整个检查，直接关闭面板
                      extension.storage.expandedBlocks.delete(expandedId)
                      extension.storage.allowedRangePanels?.delete(expandedId)
                      if (ph) {
                        try { ph.remove() } catch (_) { /* noop */ }
                      }
                      setTimeout(() => updateInlineComments(), 100)
                    },
                    onEditComment: (commentId: string, content: string) => {
                      extension.editor?.commands.editComment(commentId, content)
                    },
                    onDeleteComment: (commentId: string) => {
                      ;(extension.storage as any).suppressNextSelection = true
                      setTimeout(() => {
                        ;(extension.storage as any).suppressNextSelection = false
                      }, 150)
                      const delComment =
                        extension.storage.comments.get(commentId)

                      const ph = ensurePlaceholder()
                      if (ph) {
                        const prev = floatingPanels.get(expandedId)
                        if (prev) {
                          floatingPanels.set(expandedId, { ...prev, referenceElement: ph })
                          renderFloatingPanels()
                        }
                      }

                      extension.editor?.commands.deleteComment(commentId)

                      // 若删除的是父评论(即当前 range 主评论)或本地已无记录, 关闭面板
                      if (!delComment || !delComment.parentId) {
                        extension.storage.expandedBlocks.delete(expandedId)
                        extension.storage.allowedRangePanels?.delete(expandedId)
                      }

                      setTimeout(() => updateInlineComments(), 100)
                    },
                    referenceElement: markedElement,
                  })

                  renderFloatingPanels()
                }

                console.log(`About to call showRangePanel for ${expandedId}`)
                showRangePanel()

                return // Skip block processing for range comments
              }

              // Regular block comment processing
              const blockId = expandedId
              const blockEl = editorElement.querySelector(
                `[data-id="${blockId}"], [id="${blockId}"]`
              )
              if (!blockEl) {
                return
              }

              // Get comments for this block (including replies)
              const blockComments: Comment[] = []
              const commentIds = new Set<string>()

              // First get parent comments (excluding resolved/dismissed)
              extension.storage.comments.forEach((comment) => {
                if (
                  (comment.docBlockId || (comment as any)?.metadata?.blockId) === blockId &&
                  !comment.parentId && // Only parent comments
                  (comment.scope === 'BLOCK' || comment.scope === 'RANGE') &&
                  comment.commentStatus !== 'RESOLVED' &&
                  comment.commentStatus !== 'DISMISSED' &&
                  comment.commentStatus !== 'IMPLEMENTED'
                ) {
                  commentIds.add(comment.id)
                  blockComments.push(comment)
                }
              })

              // Then get ALL replies (including nested replies) - keep checking until no new replies are found
              let foundNewReplies = true
              while (foundNewReplies) {
                foundNewReplies = false
                extension.storage.comments.forEach((comment) => {
                  if (
                    comment.parentId &&
                    commentIds.has(comment.parentId) &&
                    !commentIds.has(comment.id) &&
                    comment.commentStatus !== 'RESOLVED' &&
                    comment.commentStatus !== 'DISMISSED'
                  ) {
                    blockComments.push(comment)
                    commentIds.add(comment.id)
                    foundNewReplies = true
                  }
                })
              }

              // Get the range text from the first RANGE comment if any
              let rangeText: string | undefined
              const firstRangeComment = blockComments.find(
                (c) => c.scope === 'RANGE' && c.textRange?.text
              )
              if (firstRangeComment && firstRangeComment.textRange?.text) {
                rangeText = firstRangeComment.textRange.text
              }

              // Show floating comment panel for block
              // Note: We show block panels even if empty to allow adding new comments
              const showBlockPanel = () => {
                floatingPanels.set(blockId, {
                  blockId,
                  comments: blockComments,
                  rangeText, // Pass range text if available
                  isRangeComment: false, // This is a block panel, not a range comment panel
                  referenceElement: blockEl as HTMLElement,
                  // 仅展示内容（隐藏回复与操作按钮），但保留新增 Block 评论输入
                  showContent: true,
                  // 新增：浮窗内点击线程时，单向通知右侧面板跳转高亮
                  onThreadActivate: (activatedId: string) => {
                    try {
                      extension.options.onCommentActivated?.(activatedId)
                    } catch (_e) { /* noop */ }
                  },
                  onAddComment: (content: string, extra?: { mentions?: string[]; richContent?: any }) => {
                    extension.editor?.commands.addBlockComment(blockId, content, extra)
                    // Keep the block expanded after adding comment
                    setTimeout(() => updateInlineComments(), 100)
                  },
                  onReply: (parentId: string, content: string) => {
                    extension.options.storage
                      .createReply(parentId, content)
                      .then((reply) => {
                        // Add to local storage immediately
                        if (reply) {
                          extension.storage.comments.set(reply.id, reply)
                          // Store current expanded blocks before updating
                          const currentExpanded = Array.from(
                            extension.storage.expandedBlocks
                          )
                          extension.options.onCommentsUpdated(
                            Array.from(extension.storage.comments.values())
                          )
                          // Restore expanded blocks after update
                          currentExpanded.forEach((id) =>
                            extension.storage.expandedBlocks.add(id)
                          )
                        }

                        // Update the UI without reloading (keeps panel open)
                        setTimeout(() => updateInlineComments(), 100)
                      })
                      .catch((error) => {
                        console.error('Failed to create reply:', error)
                        if (extension.options.onError) {
                          if (
                            error.message?.includes('network') ||
                            error.message?.includes('offline')
                          ) {
                            extension.options.onError(
                              "Reply saved locally. It will sync when you're back online.",
                              error
                            )
                          } else {
                            extension.options.onError(
                              'Failed to create reply. Please try again.',
                              error
                            )
                          }
                        }
                      })
                  },
                  onResolve: (
                    commentId: string,
                    resolution: 'RESOLVED' | 'DISMISSED' | 'IMPLEMENTED' = 'RESOLVED'
                  ) => {
                    extension.editor?.commands.commentResolve(
                      commentId,
                      resolution
                    )

                    // Update the comment status locally
                    const comment = extension.storage.comments.get(commentId)
                    if (comment) {
                      extension.storage.comments.set(commentId, {
                        ...comment,
                        commentStatus: resolution,
                      })
                    }

                    // Check if there are any remaining non-resolved comments for this block
                    const remainingComments = blockComments.filter(
                      (c) =>
                        c.id !== commentId &&
                        c.commentStatus !== 'RESOLVED' &&
                        c.commentStatus !== 'DISMISSED' &&
                        c.commentStatus !== 'IMPLEMENTED'
                    )

                    if (remainingComments.length === 0) {
                      // Close the panel if no more comments
                      extension.storage.expandedBlocks.delete(blockId)
                    }

                    // Update the panel
                    setTimeout(() => updateInlineComments(), 100)
                  },
                  onClose: () => {
                    extension.storage.expandedBlocks.delete(blockId)
                    updateInlineComments()

                    // Force decoration update to remove block highlight
                    if (extension.editor) {
                      const { state, dispatch } = extension.editor.view
                      const tr = state.tr.setMeta('addToHistory', false)
                      dispatch(tr)
                    }
                  },
                  onApplySuggestion: (commentId: string) => {
                    try {
                      const ok = extension.editor?.chain().focus().applySuggestion(commentId).run()
                      if (!ok) {
                    extension.editor?.commands.applySuggestion(commentId)
                      }
                    } catch (_e) {
                      extension.editor?.commands.applySuggestion(commentId)
                    }
                  },
                  onDismissCheck: (commentId: string) => {
                    extension.editor?.commands.dismissCheck(commentId)
                  },
                  onEditComment: (commentId: string, content: string) => {
                    extension.editor?.commands.editComment(commentId, content)
                  },
                  onDeleteComment: (commentId: string) => {
                    extension.editor?.commands.deleteComment(commentId)
                    // Keep the panel open after deletion so users can add more comments
                    setTimeout(() => updateInlineComments(), 100)
                  },
                })

                renderFloatingPanels()
              }

              showBlockPanel()
            })

            // Clean up panels for blocks that are no longer expanded
            floatingPanels.forEach((_, panelId) => {
              if (
                !extension.storage.expandedBlocks.has(panelId) &&
                !panelId.startsWith('add-')
              ) {
                floatingPanels.delete(panelId)
              }
            })

            // Clean up add comment panel if no longer needed
            if (!extension.storage.pendingNewComment) {
              floatingPanels.forEach((_, panelId) => {
                if (panelId.startsWith('add-')) {
                  floatingPanels.delete(panelId)
                }
              })
            }

            // Re-render if any panels were removed
            renderFloatingPanels()
          }

          const updateCommentIndicators = () => {
            const editorElement = extension.editor?.view?.dom
            if (!editorElement) return

            // Create container if it doesn't exist
            if (!indicatorContainer) {
              indicatorContainer = createIndicatorContainer()
              if (!indicatorContainer) return
            }

            // Clear existing indicators
            indicatorContainer.innerHTML = ''

            // Guard: if multiple containers somehow exist (e.g., after doc switch), keep the latest
            const containers = (extension.editor?.view?.dom as HTMLElement)
              ?.parentElement?.querySelectorAll('.comment-indicators-container')
            if (containers && containers.length > 1) {
              containers.forEach((el, idx) => {
                if (idx > 0) try { el.remove() } catch { /* noop */ }
              })
            }

            // Find all blocks with data-id attribute or id attribute
            const blocksWithId =
              editorElement.querySelectorAll('[data-id], [id]')

            // Process each block
            blocksWithId.forEach((blockEl) => {
              // Skip inline elements and non-block nodes
              const nodeType =
                blockEl.getAttribute('data-type') ||
                blockEl.tagName.toLowerCase()
              if (['text', 'span', 'mark', 'a'].includes(nodeType)) return

              const blockId =
                blockEl.getAttribute('data-id') || blockEl.getAttribute('id')
              if (!blockId) return

              // Get block position relative to editor wrapper
              const blockRect = blockEl.getBoundingClientRect()
              const containerRect = indicatorContainer!.getBoundingClientRect()
              const relativeTop = blockRect.top - containerRect.top

              // Check if this block has comments
              const hasComments =
                blockEl.classList.contains('has-block-comments')
                // 如果 readOnly 为 true 不显示下面的东西
                if(extension?.options?.readOnly){
                  return
                }

              if (hasComments) {
                // Get comments for this block - must match the logic in updateInlineComments
                const visibleComments: Comment[] = []
                const parentCommentIds = new Set<string>()

                // First get non-resolved parent comments
                extension.storage.comments.forEach((comment) => {
                  if (
                    (comment.docBlockId || (comment as any)?.metadata?.blockId) === blockId &&
                    !comment.parentId &&
                    comment.commentStatus !== 'RESOLVED' &&
                    comment.commentStatus !== 'DISMISSED' &&
                    comment.commentStatus !== 'IMPLEMENTED' &&
                    (comment.scope === 'BLOCK' || comment.scope === 'RANGE')
                  ) {
                    visibleComments.push(comment)
                    parentCommentIds.add(comment.id)
                  }
                })

                // Then get replies to visible parents (recursive)
                let foundNewReplies = true
                while (foundNewReplies) {
                  foundNewReplies = false
                  extension.storage.comments.forEach((comment) => {
                    if (
                      comment.parentId &&
                      parentCommentIds.has(comment.parentId) &&
                      !parentCommentIds.has(comment.id) &&
                      comment.commentStatus !== 'RESOLVED' &&
                      comment.commentStatus !== 'DISMISSED'
                    ) {
                      visibleComments.push(comment)
                      parentCommentIds.add(comment.id)
                      foundNewReplies = true
                    }
                  })
                }

                const comments = visibleComments

                if (comments.length === 0) return

                // For tooltip, count only parent comments
                const parentComments = comments.filter((c) => !c.parentId)
                const blockCommentCount = parentComments.filter(
                  (c) => c.scope === 'BLOCK'
                ).length
                const inlineCommentCount = parentComments.filter(
                  (c) => c.scope === 'RANGE'
                ).length

                // Create indicator button for existing comments
                const indicator = document.createElement('button')
                indicator.className =
                  'block-comment-indicator' +
                  (extension.storage.expandedBlocks.has(blockId)
                    ? ' expanded'
                    : '')
                indicator.style.position = 'absolute'
                indicator.style.top = `${relativeTop + 5}px`
                indicator.style.right = '-20px'
                indicator.style.pointerEvents = 'auto'
                // Prevent ProseMirror from hijacking the first click (focus/selection changes)
                indicator.setAttribute('type', 'button')
                indicator.tabIndex = -1
                try {
                  indicator.addEventListener(
                    'pointerdown',
                    (e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    },
                    { capture: true }
                  )
                } catch (_e) { /* noop */ }
                indicator.addEventListener(
                  'mousedown',
                  (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  },
                  true
                )
                try {
                  indicator.addEventListener(
                    'touchstart',
                    (e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    },
                    { passive: false, capture: true }
                  )
                } catch (_e) { /* noop */ }

                // Create tooltip text
                const tooltipParts = []
                if (blockCommentCount > 0) {
                  tooltipParts.push(
                    `${blockCommentCount} block comment${blockCommentCount > 1 ? 's' : ''}`
                  )
                }
                if (inlineCommentCount > 0) {
                  tooltipParts.push(
                    `${inlineCommentCount} inline comment${inlineCommentCount > 1 ? 's' : ''}`
                  )
                }
                indicator.setAttribute('title', tooltipParts.join(', '))

                indicator.innerHTML = `
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M8.66667 5.33333H4.66667M11.3333 8H4.66667M14 10C14 10.3536 13.8595 10.6928 13.6095 10.9428C13.3594 11.1929 13.0203 11.3333 12.6667 11.3333H4.66667L2 14V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H12.6667C13.0203 2 13.3594 2.14048 13.6095 2.39052C13.8595 2.64057 14 2.97971 14 3.33333V10Z" stroke="currentColor" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                  <span class="comment-count">${parentComments.length}</span>
                `

                indicator.onclick = (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  try { extension.editor?.view.focus() } catch (_e) { /* noop */ }

                  // If panel was closed outside (not via toggle), expanded state can be stale.
                  // Detect and treat this click as an open (not a close).
                  try {
                    const panels = document.getElementById('tiptap-comment-panels')
                    const hasDomPanelForThis = !!panels?.querySelector(
                      `[data-block-id="${blockId}"]`
                    )
                    const hasInMemoryPanel = floatingPanels.has(blockId)
                    const isExpanded = extension.storage.expandedBlocks.has(blockId)
                    const stale = isExpanded && !(hasDomPanelForThis || hasInMemoryPanel)

                    if (stale) {
                      // Force open current block (non-toggle)
                      extension.storage.expandedBlocks.clear()
                      extension.storage.expandedBlocks.add(blockId)
                      updateInlineComments()

                      indicator.classList.add('expanded')
                      if (extension.editor) {
                        const { state, dispatch } = extension.editor.view
                        const tr = state.tr.setMeta('addToHistory', false)
                        dispatch(tr)
                      }
                      return
                    }
                  } catch (_e) { /* noop */ }

                  // Normal toggle behaviour
                  toggleInlineComments(blockId)

                  // Immediate re-render to avoid async timing
                  try { updateInlineComments() } catch (_e) { /* noop */ }

                  // Update indicator appearance and force re-render decorations
                  if (extension.storage.expandedBlocks.has(blockId)) {
                    indicator.classList.add('expanded')
                  } else {
                    indicator.classList.remove('expanded')
                  }

                  // Force decoration update
                  if (extension.editor) {
                    const { state, dispatch } = extension.editor.view
                    const tr = state.tr.setMeta('addToHistory', false)
                    dispatch(tr)
                  }

                  // Strong fallback: re-check multiple times to guarantee panel shows
                  const ensurePanelVisible = () => {
                    try {
                      const isExpandedNow = extension.storage.expandedBlocks.has(blockId)
                      if (!isExpandedNow) return
                      const panels = document.getElementById('tiptap-comment-panels')
                      const hasDom = !!panels?.querySelector(`[data-block-id="${blockId}"]`)
                      const inMem = floatingPanels.has(blockId)
                      if (!(hasDom || inMem)) {
                        // force keep expanded and re-render again
                        extension.storage.expandedBlocks.add(blockId)
                        try { updateInlineComments() } catch (_e) { /* noop */ }
                      }
                    } catch (_e) { /* noop */ }
                  }

                  try { requestAnimationFrame(() => ensurePanelVisible()) } catch (_e) { /* noop */ }
                  setTimeout(() => ensurePanelVisible(), 60)
                  setTimeout(() => ensurePanelVisible(), 160)
                }

                if (indicatorContainer) {
                  indicatorContainer.appendChild(indicator)
                }
              } else {

                
                // Create "Add comment" button for blocks without comments
                const addButton = document.createElement('button')
                addButton.className = 'block-comment-add-button'
                addButton.style.position = 'absolute'
                addButton.style.top = `${relativeTop}px`
                addButton.style.right = '-20px'
                addButton.style.pointerEvents = 'auto'
                addButton.setAttribute('title', 'Add comment')
                addButton.setAttribute('data-block-id', blockId)
                // Prevent first click being consumed by editor focus/selection changes
                addButton.setAttribute('type', 'button')
                addButton.tabIndex = -1
                try {
                  addButton.addEventListener(
                    'pointerdown',
                    (e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    },
                    { capture: true }
                  )
                } catch (_e) { /* noop */ }
                addButton.addEventListener(
                  'mousedown',
                  (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  },
                  true
                )
                try {
                  addButton.addEventListener(
                    'touchstart',
                    (e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    },
                    { passive: false, capture: true }
                  )
                } catch (_e) { /* noop */ }

                // Set initial visibility - only show if this block is hovered
                addButton.style.opacity =
                  hoveredBlockId === blockId ? '0.5' : '0'

                addButton.innerHTML = `
                  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
  <path d="M8.5 4.66667V8.66667M6.5 6.66667H10.5M14.5 10C14.5 10.3536 14.3595 10.6928 14.1095 10.9428C13.8594 11.1929 13.5203 11.3333 13.1667 11.3333H5.16667L2.5 14V3.33333C2.5 2.97971 2.64048 2.64057 2.89052 2.39052C3.14057 2.14048 3.47971 2 3.83333 2H13.1667C13.5203 2 13.8594 2.14048 14.1095 2.39052C14.3595 2.64057 14.5 2.97971 14.5 3.33333V10Z" stroke="currentColor" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                `

                // Handle hover on the button itself
                addButton.onmouseenter = () => {
                  addButton.style.opacity = '0.8'
                }
                addButton.onmouseleave = () => {
                  addButton.style.opacity =
                    hoveredBlockId === blockId ? '0.5' : '0'
                }

                addButton.onclick = (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  try { extension.editor?.view.focus() } catch (_e) { /* noop */ }

                  // Close any existing panels and open this one
                  extension.storage.expandedBlocks.clear()
                  extension.storage.expandedBlocks.add(blockId)
                  updateInlineComments()

                  // Force decoration update to show block highlight
                  if (extension.editor) {
                    const { state, dispatch } = extension.editor.view
                    const tr = state.tr.setMeta('addToHistory', false)
                    dispatch(tr)
                  }
                }

                if (indicatorContainer) {
                  indicatorContainer.appendChild(addButton)
                } else {
                  console.error('indicatorContainer is null')
                }
              }
            })
          }

          // Initial setup
          setTimeout(() => {
            updateCommentIndicators()
            // Don't show inline comments by default

            // Listen for scroll and hover events on the editor
            const editorElement = extension.editor?.view?.dom

            if (editorElement) {
              editorElement.addEventListener('scroll', updateCommentIndicators)
            }

            if (editorElement) {
              // Track hover state on individual blocks
              const handleMouseMove = (e: MouseEvent) => {
                // Find the block element under the mouse
                const target = e.target as HTMLElement
                const blockEl = target.closest('[data-id]')

                if (blockEl) {
                  const newHoveredId = blockEl.getAttribute('data-id')

                  if (newHoveredId !== hoveredBlockId) {
                    // Hide previous button
                    if (hoveredBlockId) {
                      const prevButton = indicatorContainer?.querySelector(
                        `[data-block-id="${hoveredBlockId}"]`
                      )
                      if (
                        prevButton &&
                        prevButton.classList.contains(
                          'block-comment-add-button'
                        )
                      ) {
                        ;(prevButton as HTMLElement).style.opacity = '0'
                      }
                    }

                    // Show new button
                    hoveredBlockId = newHoveredId
                    const newButton = indicatorContainer?.querySelector(
                      `[data-block-id="${hoveredBlockId}"]`
                    )
                    if (
                      newButton &&
                      newButton.classList.contains('block-comment-add-button')
                    ) {
                      ;(newButton as HTMLElement).style.opacity = '0.5'
                    }
                  }
                } else {
                  // Not hovering over any block
                  if (hoveredBlockId) {
                    const prevButton = indicatorContainer?.querySelector(
                      `[data-block-id="${hoveredBlockId}"]`
                    )
                    if (
                      prevButton &&
                      prevButton.classList.contains('block-comment-add-button')
                    ) {
                      ;(prevButton as HTMLElement).style.opacity = '0'
                    }
                    hoveredBlockId = null
                  }
                }
              }

              const handleMouseLeave = () => {
                // Hide any visible add button when leaving the editor
                if (hoveredBlockId) {
                  const button = indicatorContainer?.querySelector(
                    `[data-block-id="${hoveredBlockId}"]`
                  )
                  if (
                    button &&
                    button.classList.contains('block-comment-add-button')
                  ) {
                    ;(button as HTMLElement).style.opacity = '0'
                  }
                  hoveredBlockId = null
                }
              }

              editorElement.addEventListener('mousemove', handleMouseMove)
              editorElement.addEventListener('mouseleave', handleMouseLeave)

              // Handle Escape key to close all comment panels
              const handleKeyDown = (e: KeyboardEvent) => {
                if (
                  e.key === 'Escape' &&
                  extension.storage.expandedBlocks.size > 0
                ) {
                  e.preventDefault()
                  e.stopPropagation()
                  extension.storage.expandedBlocks.clear()
                  updateInlineComments()

                  // Force decoration update
                  if (extension.editor) {
                    const { state, dispatch } = extension.editor.view
                    const tr = state.tr.setMeta('addToHistory', false)
                    dispatch(tr)
                  }
                }
              }

              document.addEventListener('keydown', handleKeyDown)

              // Store cleanup function
              cleanupFunctions.push(() => {
                editorElement.removeEventListener('mousemove', handleMouseMove)
                editorElement.removeEventListener(
                  'mouseleave',
                  handleMouseLeave
                )
                document.removeEventListener('keydown', handleKeyDown)
              })
            }
          }, 100)

          return {
            update(_view, _prevState) {
              // Clear existing timeout
              if (updateTimeout) {
                clearTimeout(updateTimeout)
              }

              // Check if we need immediate update
              if (extension.storage.needsInlineCommentUpdate) {
                extension.storage.needsInlineCommentUpdate = false
                updateCommentIndicators()
                updateInlineComments()
                return
              }

              // Debounce updates
              updateTimeout = setTimeout(() => {
                updateCommentIndicators()
                updateInlineComments() // Will only show for expanded blocks
              }, 50)
            },
            destroy() {
              if (updateTimeout) {
                clearTimeout(updateTimeout)
              }
              if (indicatorContainer) {
                indicatorContainer.remove()
                indicatorContainer = null
              }

              const editorElement = extension.editor?.view?.dom
              if (editorElement) {
                editorElement.removeEventListener(
                  'scroll',
                  updateCommentIndicators
                )
              }

              // Call all cleanup functions
              cleanupFunctions.forEach((fn) => fn())
              cleanupFunctions = []

              // Clean up floating panels
              floatingPanels.clear()
              if (floatingPanelRoot) {
                // Defer unmount to avoid React 19 warning about unmounting during render
                const rootToUnmount = floatingPanelRoot
                const containerToRemove = floatingPanelContainer
                queueMicrotask(() => {
                  rootToUnmount.unmount()
                  containerToRemove?.remove()
                })
                floatingPanelRoot = null
                floatingPanelContainer = null
              }

              // Remove scroll event listeners if they exist
              if (extension.storage.scrollHandler) {
                const preventScroll = extension.storage.scrollHandler
                const scrollOptions = { passive: false, capture: true }

                const scrollArea = document.getElementById('topic-scroll-area')
                if (scrollArea) {
                  scrollArea.removeEventListener(
                    'wheel',
                    preventScroll,
                    scrollOptions
                  )
                  scrollArea.removeEventListener(
                    'touchmove',
                    preventScroll,
                    scrollOptions
                  )
                }

                document.removeEventListener(
                  'wheel',
                  preventScroll,
                  scrollOptions
                )
                document.removeEventListener(
                  'touchmove',
                  preventScroll,
                  scrollOptions
                )

                extension.storage.scrollHandler = null
              }
            },
          }
        },
      }),
    ]
  },

  addCommands() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const extension = this

    // Helper: find nearest ancestor block id from a given document position
    function getNearestBlockId(state: any, from: number): string | undefined {
      try {
        const $from = state.doc.resolve(from)
        for (let d = $from.depth; d >= 0; d--) {
          const node = $from.node(d) as any
          const nid = node?.attrs?.id || node?.attrs?.['data-id']
          if (typeof nid === 'string' && nid.length > 0) return nid
        }
      } catch (_e) { /* noop */ }
      return undefined
    }

    // Helper: find the absolute position of the node with given blockId
    function findBlockPos(state: any, blockId: string): number | null {
      let found: number | null = null
      state.doc.descendants((node: any, pos: number) => {
        const nid = node?.attrs?.id || node?.attrs?.['data-id']
        if (nid === blockId) {
          found = pos
          return false
        }
        return true
      })
      return found
    }

    // Helper to sync marks with comments
    const syncMarks = debounce(() => {
      if (!extension.editor || !extension.storage.comments) return

      const { state, dispatch } = extension.editor.view
      const { tr } = state
      const docSize = state.doc.content.size

      // Don't try to sync if document is empty
      if (docSize === 0) {
        return
      }

      // First, collect all existing comment marks
      const existingMarks = new Map<string, { from: number; to: number }[]>()
      state.doc.descendants((node, pos) => {
        node.marks.forEach((mark) => {
          if (mark.type.name === 'comment' && mark.attrs.commentId) {
            const commentId = mark.attrs.commentId
            if (!existingMarks.has(commentId)) {
              existingMarks.set(commentId, [])
            }
            existingMarks
              .get(commentId)!
              .push({ from: pos, to: pos + node.nodeSize })
          }
        })
      })

      // Apply marks for RANGE comments
      Array.from(extension.storage.comments.values()).forEach((comment) => {
        // Skip replies - only parent comments should have marks
        if (comment.commentType === 'REPLY') {
          return
        }

        if (
          comment.scope === 'RANGE' &&
          comment.textRange &&
          comment.commentStatus !== 'RESOLVED' &&
          comment.commentStatus !== 'DISMISSED' &&
          comment.commentStatus !== 'IMPLEMENTED'
        ) {
          // Check if mark already exists for this comment
          if (existingMarks.has(comment.id)) {
            return
          }

          let markApplied = false

          // First, try block-relative positioning (new system)
          if (comment.docBlockId) {
            // Find the block node in the document
            state.doc.descendants((node, pos) => {
              const nodeId = node.attrs.id || node.attrs['data-id']
              if (nodeId === comment.docBlockId) {
                // Calculate absolute positions from block-relative positions
                let textOffset = 0
                let absoluteFrom = -1
                let absoluteTo = -1

                // Traverse the block's content to find text positions
                node.descendants((childNode, childPos) => {
                  if (childNode.isText) {
                    const nodeStart = pos + childPos + 1 // +1 for the parent node
                    const nodeTextLength = childNode.text?.length || 0

                    // Check if our range starts in this text node
                    if (
                      absoluteFrom === -1 &&
                      textOffset + nodeTextLength > comment.textRange!.start
                    ) {
                      absoluteFrom =
                        nodeStart + (comment.textRange!.start - textOffset)
                    }

                    // Check if our range ends in this text node
                    if (
                      absoluteTo === -1 &&
                      textOffset + nodeTextLength >= comment.textRange!.end
                    ) {
                      absoluteTo =
                        nodeStart + (comment.textRange!.end - textOffset)
                      return false // Stop searching
                    }

                    textOffset += nodeTextLength
                  }
                })

                // Apply mark if valid
                if (absoluteFrom >= 0 && absoluteTo > absoluteFrom) {
                  console.log('Block-relative mark application:', {
                    commentId: comment.id,
                    blockId: comment.docBlockId,
                    relativeRange: {
                      start: comment.textRange!.start,
                      end: comment.textRange!.end,
                    },
                    absoluteRange: { from: absoluteFrom, to: absoluteTo },
                    expectedText: comment.textRange!.text,
                    actualText: state.doc.textBetween(absoluteFrom, absoluteTo),
                  })
                  markApplied = tryApplyMark(absoluteFrom, absoluteTo, comment)
                }

                return false // Stop searching
              }
            })
          }

          // Fallback: try absolute positioning (old system) if block-relative failed
          if (
            !markApplied &&
            comment.textRange &&
            comment.textRange.start < docSize &&
            comment.textRange.end <= docSize
          ) {
            markApplied = tryApplyMark(
              comment.textRange.start,
              comment.textRange.end,
              comment
            )
          }

          // Last resort: text search within the specific block
          if (!markApplied && comment.textRange.text && comment.docBlockId) {
            const searchText = comment.textRange.text

            // First find the block
            state.doc.descendants((node, pos) => {
              const nodeId = node.attrs.id || node.attrs['data-id']
              if (nodeId === comment.docBlockId) {
                // Search for text only within this block
                node.descendants((childNode, childPos) => {
                  if (
                    childNode.isText &&
                    childNode.text?.includes(searchText)
                  ) {
                    const index = childNode.text.indexOf(searchText)
                    if (index !== -1) {
                      const from = pos + childPos + 1 + index // +1 for parent node
                      const to = from + searchText.length
                      markApplied = tryApplyMark(from, to, comment)
                      return false // Stop searching
                    }
                  }
                })
                return false // Stop searching blocks
              }
            })
          }

          if (!markApplied) {
            console.warn(
              `syncMarks: Failed to apply mark for comment ${comment.id}`
            )
          }
        }
      })

      // Helper function to apply mark
      function tryApplyMark(
        from: number,
        to: number,
        comment: Comment
      ): boolean {
        if (from >= 0 && to > from && to <= docSize) {
          // Verify the text at this range matches what we expect
          if (comment.textRange?.text) {
            const actualText = state.doc.textBetween(from, to)
            if (actualText !== comment.textRange.text) {
              // If text doesn't match exactly, try to find the exact text nearby
              const searchStart = Math.max(0, from - 10)
              const searchEnd = Math.min(docSize, to + 10)
              const searchText = state.doc.textBetween(searchStart, searchEnd)
              const exactIndex = searchText.indexOf(comment.textRange.text)

              if (exactIndex !== -1) {
                // Found the exact text nearby, adjust the range
                const newFrom = searchStart + exactIndex
                const newTo = newFrom + comment.textRange.text.length
                console.log('Adjusting mark position for comment', comment.id, {
                  oldRange: { from, to },
                  newRange: { from: newFrom, to: newTo },
                  text: comment.textRange.text,
                })
                from = newFrom
                to = newTo
              } else {
                console.warn('Text not found for comment', comment.id, {
                  expected: comment.textRange.text,
                  searchedIn: searchText,
                  range: { from, to },
                })
                return false
              }
            }
          }

          // More thorough check if mark already exists in this exact range
          let markExists = false
          let exactRangeExists = false

          state.doc.nodesBetween(from, to, (node, nodePos) => {
            const commentMark = node.marks.find(
              (m) =>
                m.type.name === 'comment' && m.attrs.commentId === comment.id
            )
            if (commentMark) {
              markExists = true
              // Check if this is the exact range we're trying to mark
              if (nodePos >= from && nodePos + node.nodeSize <= to) {
                exactRangeExists = true
              }
            }
          })

          if (exactRangeExists) {
            return true
          }

          if (!markExists) {
            const mark = state.schema.marks.comment.create({
              commentId: comment.id,
              source: comment.source,
              severity: comment.metadata?.severity,
            })
            tr.addMark(from, to, mark)
            console.log('Applied mark for comment', comment.id, 'at range', {
              from,
              to,
            })
            return true
          } else {
            console.log('Mark already exists for comment', comment.id)
            return true
          }
        }

        return false
      }

      // Clean up marks for comments that should not be marked
      existingMarks.forEach((positions, commentId) => {
        const comment = extension.storage.comments.get(commentId)
        // Remove marks if comment doesn't exist, is a reply, or is in a terminal status
        const isTerminal =
          comment?.commentStatus === 'RESOLVED' ||
          comment?.commentStatus === 'DISMISSED' ||
          comment?.commentStatus === 'IMPLEMENTED'
        if (!comment || comment.commentType === 'REPLY' || isTerminal) {
          positions.forEach(({ from, to }) => {
            state.doc.nodesBetween(from, to, (node, pos) => {
              const mark = node.marks.find(
                (m) =>
                  m.type.name === 'comment' && m.attrs.commentId === commentId
              )
              if (mark) {
                tr.removeMark(pos, pos + node.nodeSize, mark)
              }
            })
          })
        }
      })

      if (tr.docChanged) {
        dispatch(tr)
      } else {
        console.log('tr.docChanged is false')
      }
    }, 100)

    const refreshCommentsFromServer = () => {
      try {
        extension.options.storage
          .clearCommentsCache(
            extension.options.docId,
            extension.options.branchId,
            extension.options.changeRequestId
          )
          .catch(() => {})
          .finally(() => {
            try {
              extension.editor?.commands.reloadComments(true)
            } catch (_e) { /*noop*/ }
          })
      } catch (_e) {
        try {
          extension.editor?.commands.reloadComments(true)
        } catch (_e2) { /*noop*/ }
      }
    }

    return {
      // Helper command to create fake suggestion comments for testing
      addFakeSuggestionComment:
        () =>
        ({ state, dispatch }) => {
          const { from, to } = state.selection
          const selectedText = state.doc.textBetween(from, to)

          if (!selectedText) {
            console.log('No text selected for suggestion comment')
            return false
          }

          // Create different suggestion types based on the text
          let suggestionText = selectedText
          let checkType = 'grammar'
          let content = ''

          // Simple demo suggestions
          if (selectedText.includes('was') || selectedText.includes('were')) {
            suggestionText = selectedText.replace(/\b(was|were)\b/g, 'is')
            checkType = 'passiveVoice'
            content = `**Passive Voice**: Consider using active voice instead of passive voice.\n\n💡 **Suggestion**: ${suggestionText}`
          } else if (selectedText.split(' ').length > 20) {
            suggestionText =
              selectedText.substring(0, selectedText.indexOf(' ', 50)) + '.'
            checkType = 'brevity'
            content = `**Brevity**: This sentence is quite long and could be simplified.\n\n💡 **Suggestion**: ${suggestionText}`
          } else {
            // Default grammar suggestion
            suggestionText =
              selectedText.charAt(0).toUpperCase() + selectedText.slice(1)
            checkType = 'grammar'
            content = `**Grammar**: Sentences should start with a capital letter.\n\n💡 **Suggestion**: ${suggestionText}`
          }

          // Find block ID
          let blockId: string | undefined
          state.doc.nodesBetween(from, to, (node) => {
            if (!blockId && node.attrs?.id) {
              blockId = node.attrs.id
            }
          })

          // Create a fake suggestion comment
          const suggestionComment: Comment = {
            id: `fake_suggestion_${Date.now()}`,
            scope: 'RANGE',
            commentType: 'COMMENT',
            docId: this.options.docId,
            docBlockId: blockId,
            textRange: {
              start: from,
              end: to,
              text: selectedText,
            },
            content,
            source: 'AUTOMATIC_CHECK',
            type: 'COMMENT',
            commentStatus: 'OPEN',
            visibility: 'WORKSPACE',
            metadata: {
              checkType,
              severity: 'suggestion',
              suggestion: 'fake ' + suggestionText,
              autoGenerated: true,
            },
            user: {
              id: 'system-check',
              name: 'AI Writing Assistant',
              email: null,
              image: null,
            },
            createdAt: new Date(),
            updatedAt: new Date(),
          }

          // Add to storage
          extension.storage.comments.set(
            suggestionComment.id,
            suggestionComment
          )

          // Apply the mark
          const mark = this.type.create({
            commentId: suggestionComment.id,
            source: suggestionComment.source,
            severity: suggestionComment.metadata?.severity,
          })

          const tr = state.tr.addMark(from, to, mark)
          if (dispatch) {
            dispatch(tr)
          }

          // Notify UI
          extension.options.onCommentsUpdated(
            Array.from(extension.storage.comments.values())
          )

          console.log('Added fake suggestion comment:', suggestionComment)
          return true
        },

      setComment:
        (content?: string) =>
        ({ commands, state }) => {
          const { from, to } = state.selection
          const text = state.doc.textBetween(from, to)
          if (from === to) {
            console.error('No text selected')
            return false
          }

          // If no content provided, show the add comment panel
          if (!content) {
            // Find block ID for the selection
            const blockId: string | undefined = getNearestBlockId(state, from)

            if (!blockId) {
              console.error('Cannot add comment: no block ID found')
              return false
            }

            // Generate final id and mark selection with it
            const realId = `c${nanoid(22)}`
            commands.setMark('comment', {
              commentId: realId,
            })

            // Store pending using the final id so updateInlineComments can render add panel
            extension.storage.pendingNewComment = {
              tempId: realId,
              from,
              to,
              text,
              blockId,
            }

            // Trigger UI update; plugin will render the add panel using pendingNewComment
            if (extension.editor) {
              const { state, dispatch } = extension.editor.view
              const tr = state.tr
                .setMeta('showAddComment', realId)
                .setMeta('addToHistory', false)
              dispatch(tr)
            }

            return true
          }

          // Find block ID and calculate block-relative positions
          let blockId: string | undefined = getNearestBlockId(state, from)
          let relativeStart = -1
          let relativeEnd = -1

          if (blockId) {
            const blockPos = findBlockPos(state, blockId)
            if (blockPos !== null) {
              const blockNode = state.doc.nodeAt(blockPos)
              if (blockNode) {
                let textOffset = 0
                blockNode.descendants((childNode: any, childPos: number) => {
                  if (childNode.isText) {
                    const nodeStart = blockPos + childPos + 1
                    const nodeEnd = nodeStart + childNode.nodeSize
                    if (relativeStart === -1 && from >= nodeStart && from <= nodeEnd) {
                      relativeStart = textOffset + (from - nodeStart)
                    }
                    if (relativeEnd === -1 && to >= nodeStart && to <= nodeEnd) {
                      relativeEnd = textOffset + (to - nodeStart)
                    }
                    textOffset += childNode.text?.length || 0
                  }
                })
              }
            }
          }

          if (!blockId || relativeStart === -1 || relativeEnd === -1) {
            console.error(
              'Cannot add comment: no block ID found or invalid selection'
            )
            return false
          }

          // Apply mark immediately with final id
          const realId = `c${nanoid(22)}`
          commands.setMark('comment', {
            commentId: realId,
          })

          // Removed unused tempComment; we persist via API directly with final id

          // Create comment via API with final id
          extension.options.storage
            .createComment({
              id: realId,
              scope: 'RANGE' as CommentScope,
              docId: extension.options.docId,
              workspaceId: extension.options.workspaceId,
              docBlockId: blockId,
              textRange: { start: relativeStart, end: relativeEnd, text },
              content,
            })
            .then((comment) => {
              extension.storage.comments.set(comment.id, comment)
              extension.options.onCommentsUpdated(
                Array.from(extension.storage.comments.values())
              )

              // 创建 RANGE 评论后刷新缓存并重载
              try {
                extension.options.storage
                  .clearCommentsCache(
                    extension.options.docId,
                    extension.options.branchId,
                    extension.options.changeRequestId
                  )
                  .catch(() => {})
                  .finally(() => {
                    try {
                      extension.editor?.commands.reloadComments(true)
                    } catch (_e) { /*noop*/ }
                  })
              } catch (_e) {
                try {
                  extension.editor?.commands.reloadComments(true)
                } catch (_e2) { /*noop*/ }
              }
            })
            .catch((error) => {
              console.error('Failed to create comment:', error)
              // Remove temp comment and mark on error
              commands.unsetMark('comment')
            })

          return true
        },

      unsetComment:
        () =>
        ({ commands }) => {
          return commands.unsetMark('comment')
        },

      commentRemove:
        (
          commentId:
            | string
            | { commentId: string; skipRemote?: boolean }
        ) =>
        ({ state, dispatch }) => {
          const id =
            typeof commentId === 'string'
              ? commentId
              : commentId?.commentId
          const skipRemote =
            typeof commentId === 'string'
              ? false
              : Boolean(commentId?.skipRemote)

          // Remove mark
          state.doc.descendants((node, pos) => {
            const mark = node.marks.find(
              (m) => m.type.name === 'comment' && m.attrs.commentId === id
            )
            if (mark) {
              if (typeof dispatch === 'function') {
                dispatch(state.tr.removeMark(pos, pos + node.nodeSize, mark))
              }
            }
          })

          // Remove from storage and API
          extension.options.storage.deleteComment(id, { skipRemote }).then(() => {
            extension.storage.comments.delete(id)

            // Remove from any parent replies array
            extension.storage.comments.forEach((comment, key) => {
              if (Array.isArray(comment.replies) && comment.replies.length > 0) {
                const filteredReplies = comment.replies.filter(
                  (reply) => reply.id !== id
                )
                if (filteredReplies.length !== comment.replies.length) {
                  extension.storage.comments.set(key, {
                    ...comment,
                    replies: filteredReplies,
                  })
                }
              }
            })

            extension.options.onCommentsUpdated(
              Array.from(extension.storage.comments.values())
            )
          })
            .finally(() => {
              // Force sync marks and UI after deletion regardless of API result
              try { syncMarks() } catch (_e) { void _e }
              if (extension.editor) {
                const { state: s, dispatch: d } = extension.editor.view
                const t = s.tr.setMeta('forceCommentUpdate', true).setMeta('addToHistory', false)
                d(t)
              }
          })

          return true
        },

      commentResolve:
        (
          commentId: string,
          resolution: 'RESOLVED' | 'DISMISSED' | 'IMPLEMENTED'
        ) =>
        ({ state, dispatch }) => {
          const comment = extension.storage.comments.get(commentId)
          if (!comment) return false

          // Find block ID for panel management
          const blockId = comment.docBlockId

          // Remove mark in the editor so it no longer highlights in the document
          if (
            resolution === 'RESOLVED' ||
            resolution === 'DISMISSED' ||
            resolution === 'IMPLEMENTED'
          ) {
            let tr = state.tr
            state.doc.nodesBetween(0, state.doc.content.size, (node, pos) => {
              const mark = node.marks.find(
                (m) =>
                  m.type.name === 'comment' && m.attrs.commentId === commentId
              )
              if (mark) {
                tr = tr.removeMark(pos, pos + node.nodeSize, mark)
              }
            })
            if (typeof dispatch === 'function' && tr.docChanged) {
              dispatch(tr)
            }

            // IMPORTANT: Do not remove from storage. Keep the comment but mark it as resolved
            const updated = {
              ...comment,
              commentStatus: resolution,
              resolvedAt: new Date(),
            }
            extension.storage.comments.set(commentId, updated)
            extension.options.onCommentsUpdated(
              Array.from(extension.storage.comments.values())
            )

            // Force sync marks and decorations after status change
            try { syncMarks() } catch (_e) { void _e }
            if (extension.editor) {
              const { state: s, dispatch: d } = extension.editor.view
              const t = s.tr.setMeta('forceCommentUpdate', true).setMeta('addToHistory', false)
              d(t)
            }

            // Check if we need to close the panel
            const panelKey =
              comment.scope === 'RANGE' ? `range_${commentId}` : blockId
            if (panelKey) {
              const remainingComments = Array.from(
                extension.storage.comments.values()
              ).filter((c) => {
                if (comment.scope === 'RANGE') {
                  // Keep panel open only if there are other OPEN comments in this thread
                  return (
                    c.commentStatus === 'OPEN' &&
                    (c.id === comment.id || c.parentId === comment.id)
                  )
                } else if (blockId) {
                  // For block comments, only OPEN comments keep the panel
                  return c.docBlockId === blockId && c.commentStatus === 'OPEN'
                }
                return false
              })

              // Close panel if no more relevant comments
              if (remainingComments.length === 0) {
                // Clear the expanded state for this comment/block
                const expandKey =
                  comment.scope === 'RANGE' ? `range-${commentId}` : blockId
                if (expandKey) {
                  extension.storage.expandedBlocks.delete(expandKey)
                }

                // Close the panel directly
                const closePanel = (window as any).closeCommentPanel
                if (closePanel) {
                  closePanel(expandKey)
                }

                // Trigger UI update
                if (extension.editor) {
                  const { state, dispatch } = extension.editor.view
                  const tr = state.tr.setMeta('addToHistory', false)
                  dispatch(tr)
                }
              }
            }
          }

          // Check if this is a fake comment (doesn't need API call)
          const isFakeComment = commentId.startsWith('fake_suggestion_')

          if (isFakeComment) {
            // For fake comments, just update the UI
            extension.options.onCommentsUpdated(
              Array.from(extension.storage.comments.values())
            )
            console.log(`Fake comment ${commentId} marked as ${resolution}`)
          } else {
            // For real comments, update via API
            extension.options.storage
              .resolveComment(commentId, resolution)
              .then(() => {
                // Already updated storage above; just notify again to ensure consumers refresh
                extension.options.onCommentsUpdated(
                  Array.from(extension.storage.comments.values())
                )
                refreshCommentsFromServer()
              })
              .catch((error) => {
                console.error('Failed to resolve comment:', error)
                // Revert storage on error
                extension.storage.comments.set(commentId, comment)
                extension.options.onCommentsUpdated(
                  Array.from(extension.storage.comments.values())
                )
              })
          }

          return true
        },

      updateComments: (comments: Comment[]) => () => {
        // Clear existing comments first
        extension.storage.comments.clear()

        // Store comments with their replies preserved
        comments.forEach((comment) => {
          // Store the comment with its replies intact
          extension.storage.comments.set(comment.id, comment)

          // Debug: Log if comment has replies
          if (comment.replies && comment.replies.length > 0) {
            console.log(`Comment ${comment.id} has ${comment.replies.length} replies`)
          }
        })
        extension.options.onCommentsUpdated(comments)

        // Also update the comments data map for external consumers
        if (extension.options.onCommentsDataUpdated) {
          extension.options.onCommentsDataUpdated(extension.storage.comments)
        }

        syncMarks()

        // Force re-render of decorations by triggering a transaction
        if (extension.editor) {
          const { state, dispatch } = extension.editor.view
          const tr = state.tr.setMeta('addToHistory', false)
          dispatch(tr)
        }

        return true
      },

      reloadComments:
        (forceRefresh: boolean = false) =>
        () => {

          // Get current branch/CR context if available
          const filters = {
            docId: extension.options.docId,
            branchId: extension.options.branchId,
            changeRequestId: extension.options.changeRequestId,
            includeResolved: true, // Include resolved comments in the reload
          }

          extension.options.storage
            .loadComments(extension.options.docId, forceRefresh, filters)
            .then((comments: Comment[]) => {
              // Clear all comments
              extension.storage.comments.clear()

              // Add all loaded comments (including check comments)
              comments.forEach((comment) => {
                // Normalize comment data - ensure content field is populated
                const normalizedComment = {
                  ...comment,
                  content: comment.content || comment.text || '',
                }
                extension.storage.comments.set(comment.id, normalizedComment)
              })

              extension.options.onCommentsUpdated(
                Array.from(extension.storage.comments.values())
              )

              // Also update the comments data map for external consumers
              if (extension.options.onCommentsDataUpdated) {
                extension.options.onCommentsDataUpdated(extension.storage.comments)
              }

              // Apply marks after a short delay to ensure editor is ready
              setTimeout(() => {
                syncMarks()
                // Fallback: DOM-only refresh to ensure classes reflect latest storage (handles timing on doc switch)
                try {
                  const editorElement = extension.editor?.view?.dom as HTMLElement | undefined
                  if (editorElement) {
                    const nodes = editorElement.querySelectorAll('span[data-comment-id]')
                    nodes.forEach((n) => {
                      const el = n as HTMLElement
                      const id = el.getAttribute('data-comment-id') || ''
                      if (!id) return
                      const comment = extension.storage?.comments?.get(id)
                      // strip old source/severity classes
                      Array.from(el.classList).forEach((cls) => {
                        if (
                          cls === 'comment-automatic-check' ||
                          cls === 'comment-ai' ||
                          cls === 'comment-user' ||
                          cls.startsWith('comment-check-') ||
                          cls.startsWith('comment-source-')
                        ) {
                          el.classList.remove(cls)
                        }
                      })
                      if (comment) {
                        const source = comment.source
                        if (source) {
                          el.classList.add(`comment-source-${String(source).toLowerCase()}`)
                        }
                        if (source === 'AUTOMATIC_CHECK') {
                          el.classList.add('comment-automatic-check')
                          const sev = comment?.metadata?.severity
                          if (typeof sev === 'string' && sev.length > 0) {
                            el.classList.add(`comment-check-${sev}`)
                          }
                        }
                        if (source === 'AI') el.classList.add('comment-ai')
                        if (source === 'USER') el.classList.add('comment-user')
                      }
                    })
                  }
                } catch (_e) { /* noop */ }
              }, 100)
              // 直接应用新的评论标记
              // syncMarks()
            })
            .catch((error: any) => {
              // 发生错误时也要重置加载状态
              // extension.storage.isLoadingComments = false
              console.error('Failed to load comments:', error)

              // Notify user of the error
              if (extension.options.onError) {
                extension.options.onError(
                  'Failed to load comments. Using cached version if available.',
                  error
                )
              }
            })
          return true
        },

      testMark:
        () =>
        ({ state, dispatch }) => {
          const { tr } = state
          const mark = state.schema.marks.comment.create({
            commentId: 'test-123',
          })
          tr.addMark(1, 10, mark)
          if (typeof dispatch === 'function') {
            dispatch(tr)
          }
          return true
        },

      addPageComment: (content: string, pageSection?: string) => () => {
        // Create a temporary comment object
        const tempId = `temp_${nanoid(10)}`
        const tempComment: Comment = {
          id: tempId,
          commentType: 'COMMENT',
          scope: 'PAGE' as CommentScope,
          docId: extension.options.docId,
          pageSection,
          content: content || '',
          source: 'USER',
          type: 'COMMENT',
          commentStatus: 'OPEN',
          visibility: 'WORKSPACE',
          branchId: extension.options.branchId,
          changeRequestId: extension.options.changeRequestId,
          createdAt: new Date(),
          updatedAt: new Date(),
        }

        // Add temp comment to storage immediately
        extension.storage.comments.set(tempId, tempComment)

        // Notify UI immediately with temp comment
        extension.options.onCommentsUpdated(
          Array.from(extension.storage.comments.values())
        )

        // Create comment via API
        extension.options.storage
          .createComment({
            id: tempId,
            scope: 'PAGE' as CommentScope,
            docId: extension.options.docId,
            workspaceId: extension.options.workspaceId,
            branchId: extension.options.branchId,
            changeRequestId: extension.options.changeRequestId,
            content,
            pageSection,
          })
          .then((comment) => {
            // Replace temp comment with real one
            extension.storage.comments.delete(tempId)
            extension.storage.comments.set(comment.id, comment)
            extension.options.onCommentsUpdated(
              Array.from(extension.storage.comments.values())
            )
          })
          .catch((error) => {
            console.error('Failed to create page comment:', error)

            if (extension.options.onError) {
              if (
                error.message?.includes('network') ||
                error.message?.includes('offline')
              ) {
                extension.options.onError(
                  "Comment saved locally. It will sync when you're back online.",
                  error
                )
              } else {
                extension.options.onError(
                  'Failed to create comment. Please try again.',
                  error
                )
              }
            }

            // Keep temp comment on network error for offline support
            if (
              !error.message?.includes('network') &&
              !error.message?.includes('offline')
            ) {
              extension.storage.comments.delete(tempId)
              extension.options.onCommentsUpdated(
                Array.from(extension.storage.comments.values())
              )
            }
          })

        return true
      },

      addBlockComment: (blockId: string, content: string, extra?: { mentions?: string[]; richContent?: any }) => () => {
        // Create a temporary comment object
        const tempId = `temp_${nanoid(10)}`
        const tempComment: Comment = {
          id: tempId,
          commentType: 'COMMENT',
          scope: 'BLOCK' as CommentScope,
          docId: extension.options.docId,
          docBlockId: blockId,
          content: content || '',
          source: 'USER',
          type: 'COMMENT',
          commentStatus: 'OPEN',
          visibility: 'WORKSPACE',
          branchId: extension.options.branchId,
          changeRequestId: extension.options.changeRequestId,
          createdAt: new Date(),
          updatedAt: new Date(),
          metadata: extra?.richContent ? { content: extra.richContent } as any : undefined,
        }

        // Add temp comment to storage immediately
        extension.storage.comments.set(tempId, tempComment)

        // Notify UI immediately with temp comment
        extension.options.onCommentsUpdated(
          Array.from(extension.storage.comments.values())
        )

        // Create comment via API
        extension.options.storage
          .createComment({
            id: tempId,
            scope: 'BLOCK' as CommentScope,
            docId: extension.options.docId,
            workspaceId: extension.options.workspaceId,
            branchId: extension.options.branchId,
            changeRequestId: extension.options.changeRequestId,
            docBlockId: blockId,
            content,
            metadata: extra?.richContent ? { content: extra.richContent } as any : undefined,
            ...(extra?.mentions ? { mentions: extra.mentions } as any : {}),
          })
          .then((comment) => {
            // Replace temp comment with real one
            extension.storage.comments.delete(tempId)
            extension.storage.comments.set(comment.id, comment)
            extension.options.onCommentsUpdated(
              Array.from(extension.storage.comments.values())
            )

            // Keep the panel open after adding comment
            setTimeout(() => {
              // Trigger update by dispatching a dummy transaction
              const view = extension.editor?.view
              if (view) {
                const { state, dispatch } = view
                const tr = state.tr.setMeta('addToHistory', false)
                dispatch(tr)
              }
            }, 100)
          })
          .catch((error) => {
            console.error('Failed to create block comment:', error)

            if (extension.options.onError) {
              if (
                error.message?.includes('network') ||
                error.message?.includes('offline')
              ) {
                extension.options.onError(
                  "Comment saved locally. It will sync when you're back online.",
                  error
                )
              } else {
                extension.options.onError(
                  'Failed to create comment. Please try again.',
                  error
                )
              }
            }

            // Keep temp comment on network error for offline support
            if (
              !error.message?.includes('network') &&
              !error.message?.includes('offline')
            ) {
              extension.storage.comments.delete(tempId)
              extension.options.onCommentsUpdated(
                Array.from(extension.storage.comments.values())
              )
            }
          })

        return true
      },

      dismissCheck:
        (commentId: string) =>
        ({ commands }) => {
          const comment = extension.storage.comments.get(commentId)
          if (comment && comment.source === 'AUTOMATIC_CHECK') {
            // Use existing commentResolve command
            return commands.commentResolve(commentId, 'DISMISSED')
          }
          return false
        },

      applyCheckSuggestion:
        (commentId: string) =>
        ({ state, dispatch }) => {
          const comment = extension.storage.comments.get(commentId)
          if (
            !comment ||
            comment.source !== 'AUTOMATIC_CHECK' ||
            !comment.metadata?.suggestion
          ) {
            return false
          }

          const { textRange, metadata } = comment
          if (!textRange) return false

          // Apply the suggestion
          const tr = state.tr.replaceRangeWith(
            textRange.start,
            textRange.end,
            state.schema.text(metadata.suggestion)
          )

          if (dispatch) {
            dispatch(tr)
          }

          // Mark check as implemented using existing command
          extension.editor?.commands.commentResolve(commentId, 'IMPLEMENTED')

          return true
        },

      editComment: (commentId: string, content: string, metadata?: any) => () => {
        const comment = extension.storage.comments.get(commentId)
        if (!comment) return false

        // Update local storage immediately with a new object
        const updatedComment = {
          ...comment,
          content,
          text: content,
          updatedAt: new Date(),
          metadata: metadata ? { ...(comment.metadata || {}), content: metadata } : comment.metadata,
        }

        // Create a new Map to ensure React detects the change
        const newCommentsMap = new Map(extension.storage.comments)
        newCommentsMap.set(commentId, updatedComment)
        extension.storage.comments = newCommentsMap

        // Also update parent if this is a reply
        if (comment.parentId) {
          const parent = newCommentsMap.get(comment.parentId)
          if (parent && parent.replies) {
            const updatedReplies = parent.replies.map(r =>
              r.id === commentId ? updatedComment : r
            )
            const updatedParent = { ...parent, replies: updatedReplies }
            newCommentsMap.set(parent.id, updatedParent)
            extension.storage.comments = newCommentsMap
          }
        }

        // Trigger updates with the new Map
        extension.options.onCommentsUpdated(
          Array.from(newCommentsMap.values())
        )
        // Also trigger data update for immediate UI refresh
        extension.options.onCommentsDataUpdated(newCommentsMap)

        // Update via API
        extension.options.storage
          .updateComment(commentId, content, metadata)
          .then((updated) => {
            // Create a new Map to ensure React detects the change
            const newCommentsMap = new Map(extension.storage.comments)
            newCommentsMap.set(commentId, updated)

            // Also update parent if this is a reply
            if (comment.parentId) {
              const parent = newCommentsMap.get(comment.parentId)
              if (parent && parent.replies) {
                const updatedReplies = parent.replies.map(r =>
                  r.id === commentId ? updated : r
                )
                const updatedParent = { ...parent, replies: updatedReplies }
                newCommentsMap.set(parent.id, updatedParent)
              }
            }

            // Update the storage with the new Map
            extension.storage.comments = newCommentsMap

            extension.options.onCommentsUpdated(
              Array.from(newCommentsMap.values())
            )
            // Also trigger data update for UI refresh
            extension.options.onCommentsDataUpdated(newCommentsMap)
            try {
              extension.options.storage
                .clearCommentsCache(
                  extension.options.docId,
                  extension.options.branchId,
                  extension.options.changeRequestId
                )
                .catch(() => {})
                .finally(() => {
                  try {
                    extension.editor?.commands.reloadComments(true)
                  } catch (_e) { /*noop*/ }
                })
            } catch (_e) {
              try {
                extension.editor?.commands.reloadComments(true)
              } catch (_e2) { /*noop*/ }
            }
          })
          .catch((error) => {
            console.error('Failed to update comment:', error)
            // Revert on error with new Map
            const revertedMap = new Map(extension.storage.comments)
            revertedMap.set(commentId, comment)
            extension.storage.comments = revertedMap
            extension.options.onCommentsUpdated(
              Array.from(revertedMap.values())
            )
            extension.options.onCommentsDataUpdated(revertedMap)
          })

        return true
      },

      deleteComment:
        (
          commentId:
            | string
            | { commentId: string; skipRemote?: boolean }
        ) =>
        ({ state, dispatch }) => {
          const id =
            typeof commentId === 'string'
              ? commentId
              : commentId?.commentId
          if (!id) return false

          const skipRemote =
            typeof commentId === 'string'
              ? false
              : Boolean(commentId?.skipRemote)

          const comment = extension.storage.comments.get(id)

          // Always remove any marks with this id in a single transaction
          let tr = state.tr
          state.doc.nodesBetween(0, state.doc.content.size, (node, pos) => {
              const mark = node.marks.find(
              (m) => m.type.name === 'comment' && m.attrs.commentId === id
              )
            if (mark) {
              tr = tr.removeMark(pos, pos + node.nodeSize, mark)
              }
            })
          if (dispatch && tr.docChanged) {
            dispatch(tr)
          }

          // Remove from local storage
          extension.storage.comments.delete(id)

          // Remove reply references from parents
          extension.storage.comments.forEach((comment, key) => {
            if (Array.isArray(comment.replies) && comment.replies.length > 0) {
              const filteredReplies = comment.replies.filter(
                (reply) => reply.id !== id
              )
              if (filteredReplies.length !== comment.replies.length) {
                extension.storage.comments.set(key, {
                  ...comment,
                  replies: filteredReplies,
                })
              }
            }
          })

          // Also remove replies
          const replies = Array.from(
            extension.storage.comments.values()
          ).filter((c) => c.parentId === id)
          replies.forEach((reply) => {
            extension.storage.comments.delete(reply.id)
          })

          extension.options.onCommentsUpdated(
            Array.from(extension.storage.comments.values())
          )

          // Force sync marks and UI immediately after local deletion
          try { syncMarks() } catch (_e) { /* noop */ }
          if (extension.editor) {
            const { state: s, dispatch: d } = extension.editor.view
            const t = s.tr.setMeta('forceCommentUpdate', true).setMeta('addToHistory', false)
            d(t)
          }

          // Delete via API
          extension.options.storage
            .deleteComment(id, { skipRemote })
            .catch((error) => {
            console.error('Failed to delete comment:', error)
            // Re-add on error
            if (comment) {
            extension.storage.comments.set(id, comment)
            }
            replies.forEach((reply) => {
              extension.storage.comments.set(reply.id, reply)
            })
            extension.options.onCommentsUpdated(
              Array.from(extension.storage.comments.values())
            )
          })

          return true
        },

      clearCommentCache: () => () => {
        // Clear the comment cache for the current document
        extension.options.storage
          .clearCommentsCache(
            extension.options.docId,
            extension.options.branchId,
            extension.options.changeRequestId
          )
          .then(() => {})
          .catch((error) => {
            console.error('Failed to clear comment cache:', error)
          })
        return true
      },

      applySuggestion:
        (commentId: string) =>
        ({ state, dispatch }) => {
          const comment = extension.storage.comments.get(commentId)
          if (
            !comment ||
            comment.source !== 'AUTOMATIC_CHECK' ||
            !comment.metadata?.suggestion
          ) {
            console.log('No suggestion found for comment:', commentId)
            return false
          }

          const { textRange, metadata } = comment
          if (!textRange) {
            console.log('No text range for suggestion comment')
            return false
          }

          console.log('Applying suggestion:', {
            original: textRange.text,
            suggestion: metadata.suggestion,
            range: { from: textRange.start, to: textRange.end },
          })

          // Prepare panel key for closing later
          const panelKey = `range-${commentId}`

          // Build transaction to remove mark and replace text
          let tr = state.tr

          // 1) 优先：根据文档中的 comment 标记定位范围（聚合多段）
          let rangeFrom: number | null = null
          let rangeTo: number | null = null
          state.doc.descendants((node: any, pos: number) => {
            const hasMark = Array.isArray(node.marks)
              ? node.marks.some(
                  (m: any) =>
                    m.type?.name === 'comment' && m.attrs?.commentId === commentId
                )
              : false
            if (hasMark) {
              const nodeFrom = pos
              const nodeTo = pos + node.nodeSize
              rangeFrom = rangeFrom === null ? nodeFrom : Math.min(rangeFrom, nodeFrom)
              rangeTo = rangeTo === null ? nodeTo : Math.max(rangeTo, nodeTo)
            }
          })

          // 2) 回退：若提供了 blockId + 原文，尝试在该块内按文本搜索
          if ((rangeFrom === null || rangeTo === null) && textRange?.text && comment.docBlockId) {
            const searchText = textRange.text
            state.doc.descendants((node: any, pos: number) => {
              const nodeId = node.attrs?.id || node.attrs?.['data-id']
              if (nodeId === comment.docBlockId) {
                node.descendants((childNode: any, childPos: number) => {
                  if (childNode.isText && childNode.text?.includes(searchText)) {
                    const index = childNode.text.indexOf(searchText)
                    if (index !== -1) {
                      const f = pos + childPos + 1 + index
                      const t = f + searchText.length
                      rangeFrom = f
                      rangeTo = t
                      return false
                    }
                  }
                })
                return false
              }
            })
          }

          // 3) 再回退：若 textRange 为块内相对位置（常见于 RANGE 注释），将其转换为绝对位置
          if ((rangeFrom === null || rangeTo === null) && comment.docBlockId) {
            state.doc.descendants((node: any, pos: number) => {
              const nodeId = node.attrs?.id || node.attrs?.['data-id']
              if (nodeId === comment.docBlockId) {
                let textOffset = 0
                let absFrom = -1
                let absTo = -1
                node.descendants((childNode: any, childPos: number) => {
                  if (childNode.isText) {
                    const nodeStart = pos + childPos + 1
                    const nodeTextLength = childNode.text?.length || 0
                    if (absFrom === -1 && textOffset + nodeTextLength > textRange.start) {
                      absFrom = nodeStart + (textRange.start - textOffset)
                    }
                    if (absTo === -1 && textOffset + nodeTextLength >= textRange.end) {
                      absTo = nodeStart + (textRange.end - textOffset)
                      return false
                    }
                    textOffset += nodeTextLength
                  }
                })
                if (absFrom >= 0 && absTo > absFrom) {
                  rangeFrom = absFrom
                  rangeTo = absTo
                }
                return false
              }
              return true
            })
          }

          // 4) 最后兜底：使用绝对范围（带边界校验）
          if (rangeFrom === null || rangeTo === null) {
            const docSize = state.doc.content.size
            const safeFrom = Math.max(0, Math.min(textRange.start, docSize))
            const safeTo = Math.max(0, Math.min(textRange.end, docSize))
            if (safeFrom < safeTo) {
              rangeFrom = safeFrom
              rangeTo = safeTo
            }
          }

          if (rangeFrom === null || rangeTo === null || rangeFrom >= rangeTo) {
            console.warn('applySuggestion: 未能定位到有效范围', comment)
            return false
          }

          // 先移除该范围内与此 ID 相关的所有标记
          state.doc.nodesBetween(rangeFrom, rangeTo, (node, pos) => {
            node.marks.forEach((mark) => {
              if (
                mark.type.name === 'comment' &&
                mark.attrs.commentId === commentId
              ) {
                tr = tr.removeMark(pos, pos + node.nodeSize, mark)
              }
            })
          })

          // 计算要替换的文本（支持 metadata.value 为空串表示删除）
          const hasValue = Object.prototype.hasOwnProperty.call(metadata ?? {}, 'value')
          const valueFromMetadata = hasValue ? String((metadata as any)?.value ?? '') : undefined
          const suggestionText = valueFromMetadata !== undefined ? valueFromMetadata : String(metadata.suggestion)

          // 替换文本 / 删除
          if (suggestionText.length === 0) {
            tr = tr.delete(rangeFrom, rangeTo)
          } else {
            tr = tr.replaceRangeWith(rangeFrom, rangeTo, state.schema.text(suggestionText))
          }

          // Dispatch the transaction
          if (dispatch) {
            dispatch(tr)
          }

          // 标记该检查为已实施（触发接口与本地状态同步）
          try {
            extension.editor?.commands.commentResolve(commentId, 'IMPLEMENTED')
          } catch (_e) {
            // noop
          }

          // Update UI and close panel after transaction
          setTimeout(() => {
            extension.options.onCommentsUpdated(
              Array.from(extension.storage.comments.values())
            )

            // Close the panel directly
            const closePanel = (window as any).closeCommentPanel
            if (closePanel) {
              closePanel(panelKey)
            }

            // Force sync marks to ensure everything is cleaned up
            if (syncMarks) {
              syncMarks()
            }
          }, 100)

          console.log(`Suggestion applied for comment ${commentId}`)
          return true
        },

      // 高亮块而不打开浮窗
      highlightBlock:
        (blockId: string) =>
        () => {
          try {
            extension.storage.activeBlockId = blockId
            // 触发一次装饰刷新
            if (extension.editor) {
              const { state, dispatch } = extension.editor.view
              dispatch(state.tr.setMeta('addToHistory', false))
            }
            return true
          } catch (_e) {
            return false
          }
        },

      openComment:
        (commentId: string) =>
        ({ state, dispatch }) => {
          const comment = extension.storage.comments.get(commentId)
          console.log('openComment command:', commentId, comment)
          if (!comment) return false

          if (comment.scope === 'RANGE') {
            // Clear any existing panels and open this one
            extension.storage.expandedBlocks.clear()
            extension.storage.activeRangeCommentId = commentId
            console.log(
              'Added to extension.storage.expandedBlocks:',
              `activeRangeCommentId=${commentId}`
            )

            // Find the comment mark position and scroll to it
            let found = false
            state.doc.descendants((node: any, pos: any) => {
              if (
                node.marks.find(
                  (mark: any) =>
                    mark.type.name === 'comment' &&
                    mark.attrs.commentId === commentId
                )
              ) {
                // Set selection to the marked text
                if (dispatch) {
                  dispatch(
                    state.tr.setSelection(TextSelection.create(state.doc, pos))
                  )
                }
                found = true
                return false // Stop searching
              }
            })

            console.log('Comment mark found:', found)

            // Trigger UI update - this will force the plugin to re-render
            if (dispatch) {
              dispatch(state.tr.setMeta('forceCommentUpdate', true))
            }

            // Store a flag to trigger update on next cycle
            extension.storage.needsInlineCommentUpdate = true

            return found
          } else if (
            comment.scope === 'BLOCK' &&
            (comment.docBlockId || comment.metadata?.blockId)
          ) {
            const blockId = comment.docBlockId || comment.metadata?.blockId

            // Clear any existing panels and open this one
            extension.storage.expandedBlocks.clear()
            extension.storage.expandedBlocks.add(blockId)

            // Find the block and scroll to it
            let found = false
            state.doc.descendants((node: any, pos: any) => {
              if (node.attrs?.id === blockId) {
                // Set selection to the block
                if (dispatch) {
                  dispatch(
                    state.tr.setSelection(TextSelection.create(state.doc, pos))
                  )
                }
                found = true
                return false
              }
            })

            // Trigger UI update - this will force the plugin to re-render
            if (dispatch) {
              dispatch(state.tr.setMeta('forceCommentUpdate', true))
            }

            // Store a flag to trigger update on next cycle
            extension.storage.needsInlineCommentUpdate = true

            return found
          }

          return false
        },
    }
  },
})
